import { faker } from '@faker-js/faker'
import { createId } from '@paralleldrive/cuid2'
import { DateTime } from 'luxon'
import { DefaultBodyType, http, HttpResponse, PathParams } from 'msw'
import invariant from 'tiny-invariant'
import { SIGNED_IN_COOKIE_NAME } from '../src/constants'
import {
  AnalysisPaginatedResponse,
  AnalysisResponse,
  AnalysisStateResponse,
  AzureIntegrationRepository,
  AzureOrganization,
  AzureOrganizationPaginatedResponse,
  AzureProject,
  AzureProjectPaginatedResponse,
  AzureRepositoryPaginatedResponse,
  BitbucketRepositoryPaginatedResponse,
  BitbucketWorkspace,
  BitbucketWorkspacePaginatedResponse,
  ChangesetPaginatedResponse,
  CodingGuidelinesResponse,
  CreatePushRequestSchema,
  CreatePushResponse,
  CreateWorkflowResponse,
  ExploitChainsPhaseSchema,
  ExploitChainsResponse,
  FindingAggregateResponse,
  FindingOverlayResponse,
  FindingOverlaysResponse,
  FindingResponse,
  FindingResultResponse,
  GetPushesResponse,
  GitLabProjectPaginatedResponse,
  GitLabProjectResponse,
  IntegrationPaginatedResponse,
  IntegrationsBitbucketRepositoryResponse,
  IntegrationTypeCountsResponse,
  OrganizationPreferencesResponse,
  PatchPaginatedResponse,
  PolarisStatusLookupResponse,
  PushResponse,
  RefreshRequestResponse,
  RefreshRequestsResponse,
  Repository,
  RepositoryIntegrationPaginatedResponse,
  RepositoryPaginatedResponse,
  ResultsVerificationResponse,
  Scale,
  ScanAggregateResponse,
  ScanEmbedParameterSchema,
  ScanResponse,
  ScorecardsPhaseSchema,
  ScorecardsResponse,
  SonarProjectPaginatedResponse,
  SonarProjectResponse,
  SystemStatus,
  SystemStatusesResponse,
  ThreatModelContent,
  ThreatModelPhaseSchema,
  ThreatModelResponse,
  ToolSchema,
  UpdateOrganizationPreferencesRequestSchema,
  User,
  WorkflowResponse,
  type BranchResponse,
  type CurrentUserResponse,
  type Integration,
  type OpenGrepRuleSet,
  type OpenGrepRulesResponse,
  type Patch,
  type RepositoryRisk,
  type Tool,
} from '../src/utils/api-client/api-schemas'
import { SCAN_FINDINGS_PARAMS } from '../src/utils/api-client/query-params'
import {
  type AnalysisStreamEvent,
  type ExploitChainsStreamEvent,
  type FindingOverlaysStreamEvent,
  type OpenGrepRulesStreamEvent,
  type ThreatModelStreamEvent,
} from '../src/utils/api-client/sse-schemas'
import { invariantType } from '../src/utils/type-utils'
import { beaconHandlers, seedBeaconRuns } from './beacon-handlers'
import { EXAMPLE_SCAN_PRIORITIZATION_CONTENT } from './example-scan-prioritization'
import { EXAMPLE_SCORECARDS_RESPONSE } from './example-scorecard'
import { freeformHandlers } from './freeform-handlers'
import { generateFindingWithMultipleResults, generateFullResultsForFinding } from './generators'
import { fromFindingResponse, paginateAndFilter, SAST_FINDINGS } from './mock-findings'
import {
  FakeAnalysisCancellation,
  FakeAppScanIntegration,
  FakeAzureIntegration,
  FakeBitbucketIntegration,
  FakeBitbucketIntegrationRepository,
  FakeBitbucketWorkspace,
  FakeCheckmarxIntegration,
  FakeCreateScanResponse,
  FakeCurrentUser,
  FakeDatadogIntegration,
  FakeExploitChains,
  FakeFindingOverlayContent,
  FakeFindingOverlays,
  FakeGitHubIntegration,
  FakeGitLabIntegration,
  FakeGitlabProject,
  FakeIntegrationPaginatedResponse,
  FakeIntegrationTypeCountsResponse,
  FakeOpenGrepRules,
  FakeOpenGrepRuleSet,
  FakePaginatedBranches,
  FakePaginatedRepositories,
  FakePaginatedRepositoryIntegrations,
  FakePaginatedScales,
  FakePaginatedScans,
  FakeRefreshRequest,
  FakeRefreshRequestList,
  FakeRepository,
  FakeRepositoryBranchList,
  FakeRepositoryIntegration,
  FakeRepositoryRisk,
  FakeResultsVerificationResponse,
  FakeScale,
  FakeScan,
  FakeScanAnalysis,
  FakeScorecards,
  FakeSonarIntegration,
  FakeSonarProject,
  FakeThreatModel,
  FakeUser,
  FakeWorkflow,
  getScaleHrefForTool,
  getScaleIdForTool,
  type RecursivePartial,
} from './models'
import { pageOf, pageOfCaseInsensitiveMatches, respondWithPage, type PickerPageAnswer } from './picker-pages'
import {
  projectHandlers,
  projectStore,
  PROTOTYPE_PROJECT_SEEDS,
  seedProjectStore,
  wantsMembershipEmbed,
} from './project-handlers'
import { COOKIE_NAME } from './session-cookie'
import { exampleThreatModelContent } from './threat-model-content'
const REPOSITORY_RISK_PATH = '/api/v1/organization/repository-risk'

const exampleFindingTriageArticle = `  <details>

    The provided code snippet includes a method \`uploadFileHandler\` that checks if the uploaded file has a \`.zip\` extension, preventing non-zip files from being processed. However, the code constructs a new File object using the temporary directory and the ZIP entry's name, which could allow path traversal characters to escape the intended directory, posing a Zip Slip vulnerability. Proper validation or sanitization of the ZIP entry names is recommended to mitigate this risk.
    
    Security controls not found include strict character validation, value allowlist/whitelist validation, directory escape detection, and directory escape sanitization. The code does prevent arbitrary file extensions by checking if the file has a \`
  .zip\` extension before processing it further.

    \`\`\`java
    51:   public AttackResult uploadFileHandler(@RequestParam("uploadedFileZipSlip") MultipartFile file) {
    52:     if (!file.getOriginalFilename().toLowerCase().endsWith(".zip")) 
    53:       return failed(this).feedback("path-traversal-zip-slip.no-zip").build();
    54:     } else {
    55:       return processZipUpload(file);
    56:     }
    57:   }
    \`\`\`

    The code involves handling a ZIP file upload and extracting its contents. A new ZipFile object is created using the uploaded ZIP file, and ZIP entries are iterated over. For each entry, a new File object is created using the temporary directory and the entry's name, which could lead to a Zip Slip vulnerability if the entry's name contains path traversal characters.

    \`\`\`java
    69:       ZipFile zip = new ZipFile(uploadedZipFile.toFile());
    70:       Enumeration<? extends ZipEntry> entries = zip.entries();
    71:       while (entries.hasMoreElements()) {
    72:         ZipEntry e = entries.nextElement();
    73:         File f = new File(tmpZipDirectory.toFile(), e.getName());
    74:         InputStream is = zip.getInputStream(e);
    75:         Files.copy(is, f.toPath(), StandardCopyOption.REPLACE_EXISTING);
    \`\`\`

    No risk recommendations are available.
    
  </details>`

const exampleScaArticle = `## Vulnerability Analysis

### CVE-2024-1234: Prototype Pollution in lodash

The \`lodash\` library versions prior to 4.17.21 are vulnerable to prototype pollution via the \`merge\`, \`mergeWith\`, and \`defaultsDeep\` functions. An attacker can inject properties into \`Object.prototype\` through crafted payloads, potentially leading to arbitrary code execution or denial of service.

### Exploitability Assessment

This vulnerability is **exploitable** in the current application context because:

- The application passes user-controlled input directly to \`_.merge()\` without sanitization
- No prototype pollution guards (e.g., \`Object.freeze(Object.prototype)\`) are in place
- The affected code path is reachable from an authenticated API endpoint

### Evidence

\`\`\`javascript
// Vulnerable code path in src/utils/config-merger.js
const config = _.merge(defaultConfig, userProvidedConfig);
\`\`\`

The \`userProvidedConfig\` object originates from a JSON request body that is not validated against a schema, allowing an attacker to include \`__proto__\` properties.

### Impact

- **Confidentiality**: An attacker could read sensitive configuration values by polluting prototype properties
- **Integrity**: Application behavior can be altered by injecting unexpected properties
- **Availability**: Denial of service through prototype chain corruption

### Recommended Remediation

Upgrade \`lodash\` to version 4.17.21 or later:

\`\`\`bash
npm install lodash@^4.17.21
\`\`\`

### References

- [NVD - CVE-2024-1234](https://nvd.nist.gov/vuln/detail/CVE-2024-1234)
- [Snyk Advisory](https://security.snyk.io/vuln/SNYK-JS-LODASH-1234567)`

const exampleScaReport = `## Executive Summary

This vulnerability affects the lodash library (version 4.17.19). An attacker could exploit this vulnerability to execute arbitrary code by manipulating prototype properties.

**Risk Assessment**: EXPLOITABLE

The vulnerability is actively exploited in the wild and has known public exploits available.

## Vulnerability Details

### CVE-2024-1234: Prototype Pollution in lodash

**Severity**: Critical (CVSS 9.8)

The lodash library contains a prototype pollution vulnerability in the \`merge\` and \`mergeWith\` functions. An attacker can inject properties into Object.prototype, potentially leading to:

- Arbitrary code execution
- Denial of service
- Property injection attacks

### Affected Versions

- lodash < 4.17.21

### Attack Vector

The vulnerability can be exploited by passing specially crafted objects to the merge functions:

\`\`\`javascript
const _ = require('lodash');
_.merge({}, JSON.parse('{"__proto__":{"polluted":"yes"}}'));
console.log({}.polluted); // "yes"
\`\`\`

## Remediation

### Recommended Fix

Upgrade lodash to version 4.17.21 or later.

\`\`\`bash
npm update lodash
\`\`\`

### Workaround

If immediate upgrade is not possible, avoid using \`merge\` and \`mergeWith\` with untrusted input.

## References

- [CVE-2024-1234](https://nvd.nist.gov/vuln/detail/CVE-2024-1234)
- [Snyk Advisory](https://security.snyk.io/vuln/SNYK-JS-LODASH-1234567)
- [GitHub Security Advisory](https://github.com/advisories/GHSA-xxxx-xxxx-xxxx)`

const exampleFindingTriageReport = `# Executive Summary

The code in question processes user-provided URLs in a web application context, specifically within the \`SSRFTask2\` class, which handles HTTP POST requests. None of the security controls we look for were observed in the code flow. The user input is not appended to a base URL, which means the user has full control over the URL, increasing the risk of Server-Side Request Forgery (SSRF). It is recommended to implement strict validation and sanitization of the URL input to mitigate potential SSRF vulnerabilities.

# Code Context

Here is a snippet of the code flow that was analyzed:

src/main/java/org/owasp/webgoat/lessons/ssrf/SSRFTask2.java
\`\`\`
39: @AssignmentHints({"ssrf.hint3"})
40: public class SSRFTask2 extends AssignmentEndpoint {
41: 
42:   @PostMapping("/SSRF/task2")
43:   @ResponseBody
44:   public AttackResult completed(@RequestParam String url) {
                                    ^^^^^^^^^^^^^^^^^^^^^^^^
45:     return furBall(url);
                       ^^^
46:   }
47: 
48:   protected AttackResult furBall(String url) {
                                     ^^^^^^^^^^
49:     if (url.matches("http://ifconfig\\.pro")) {
50:       String html;
51:       try (InputStream in = new URL(url).openStream()) {
                                        ^^^
52:         html =
53:             new String(in.readAllBytes(), StandardCharsets.UTF_8)
54:                 .replaceAll("\n", "<br>"); // Otherwise the \n gets escaped in the response
55:       } catch (MalformedURLException e) {
56:         return getFailedResult(e.getMessage());

\`\`\`


# Security Controls

## Value allowlist/whitelist validation: Not Found

## Strict character validation: Not Found

# Contextual Risk Factors

## URL Components Controlled: No Append to Base URL Found

One such thing to look for would be if the user input is appended to a base URL. This could change the severity of the issue because it would suggest the user can't control important parts of the URL (like the scheme, host, or port.) More esoteric attacks may be possible in situations where the user controls portions like the path, querystring or fragment, but these will be more rare.

However, in this case, when stepping through the data flow analysis we did not find any evidence of the user input being appended to a base URL.


# Risk Recommendations


        Given the lack of security controls observed, the lack of base URL pinning, it is possible that the code is vulnerable to SSRF. Further investigation is recommended.`

export type Handler = (request: {
  request: Request
  params: PathParams
}) => HttpResponse<DefaultBodyType> | Promise<HttpResponse<DefaultBodyType>>

export { COOKIE_NAME, COOKIE_STRING, COOKIE_VALUE } from './session-cookie'

export const withAuth =
  (handler: Handler) =>
  ({ cookies, ...rest }: { cookies: Record<string, string>; request: Request; params: PathParams }) => {
    const hasSessionCookie = COOKIE_NAME in cookies
    if (!hasSessionCookie) return new HttpResponse(null, { status: 499 })
    return handler(rest)
  }

const login = () =>
  // Endpoint should always be accessed via browser navigation
  http.get('/api/auth/login', ({ cookies }) => {
    const hasSessionCookie = COOKIE_NAME in cookies
    if (hasSessionCookie) return HttpResponse.redirect('/', 302)
    return new HttpResponse(null, {
      status: 302,
      headers: {
        Location: '/',
        'Set-Cookie': [
          `${COOKIE_NAME}=${createId()}; Path=/`,
          `github_refresh_token_expires_at=${DateTime.now().plus({ months: 6 }).toISO()}; Path=/`,
        ].join('; '),
      },
    })
  })

const logout = () =>
  // Endpoint should always be accessed via browser navigation
  http.get('/api/auth/logout', () => {
    const headers = new Headers({ Location: '/login' })
    for (const name of [COOKIE_NAME, 'github_refresh_token_expires_at', SIGNED_IN_COOKIE_NAME]) {
      headers.append('Set-Cookie', `${name}=; Path=/; Max-Age=0`)
    }
    return new HttpResponse(null, { status: 302, headers })
  })

// =============================================
// Queries
// =============================================

// Unauthenticated on purpose: the login page reads system statuses before sign-in
// (the endpoint is public — see pixee-platform ISS-7770).
export const getSystemStatuses = (items?: SystemStatus[]) =>
  http.get('/api/v1/system-statuses', () =>
    HttpResponse.json({
      _links: {
        first: {
          href: '/api/v1/system-statuses',
        },
        self: {
          href: '/api/v1/system-statuses',
        },
      },
      total: items?.length ?? 0,
      page: {
        number: 0,
        size: items?.length ?? 0,
      },
      _embedded: {
        items: items ?? [],
      },
    } satisfies SystemStatusesResponse)
  )

export const getUser = (user?: Partial<User>) =>
  http.get(
    '/api/user',
    withAuth(() => {
      return HttpResponse.json(FakeUser(user))
    })
  )

export const getCurrentUser = ({ handler, ...user }: Partial<CurrentUserResponse> & { handler?: Handler } = {}) =>
  http.get('/api/v1/users/me', withAuth(handler ?? (() => HttpResponse.json(FakeCurrentUser(user)))))

let organizationPreferences: OrganizationPreferencesResponse | null = null
let organizationPreferencesEtag: string | null = null
let organizationPreferencesVersion = 0

export const getOrganizationPreferences = () =>
  http.get(
    '/api/v1/organizations/:organizationId/preferences',
    withAuth(() => {
      if (!organizationPreferences) {
        return new HttpResponse(null, { status: 204 })
      }

      return HttpResponse.json(organizationPreferences, {
        headers: organizationPreferencesEtag ? { ETag: organizationPreferencesEtag } : {},
      })
    })
  )

export const putOrganizationPreferences = () =>
  http.put(
    '/api/v1/organizations/:organizationId/preferences',
    withAuth(async ({ request }) => {
      const ifMatch = request.headers.get('If-Match')
      const ifNoneMatch = request.headers.get('If-None-Match')

      const doesPreferenceExist = organizationPreferences !== null
      if (!doesPreferenceExist) {
        if (ifMatch) {
          return new HttpResponse('Precondition Failed', { status: 412 })
        }

        if (!ifNoneMatch) {
          return new HttpResponse(null, { status: 428 })
        }

        if (ifNoneMatch !== '*') {
          return new HttpResponse('Precondition Failed', { status: 412 })
        }
      } else {
        if (ifNoneMatch) {
          return new HttpResponse('Precondition Failed', { status: 412 })
        }

        if (!ifMatch) {
          return new HttpResponse(null, { status: 428 })
        }

        if (!organizationPreferencesEtag || ifMatch !== organizationPreferencesEtag) {
          return new HttpResponse('Precondition Failed', { status: 412 })
        }
      }

      const body = UpdateOrganizationPreferencesRequestSchema.parse(await request.json())
      const nextVersion = organizationPreferencesVersion + 1
      const randomSuffix = Math.random().toString(16).slice(2)

      const organizationId = new URL(request.url).pathname.split('/').at(-2) ?? 'default-organization'
      organizationPreferences = {
        _links: {
          self: {
            href: `/api/v1/organizations/${organizationId}/preferences`,
          },
        },
        content: body.content,
        updated_at: new Date().toISOString(),
        updated_by: 'mock-user@example.com',
        org_id: organizationId,
      }
      organizationPreferencesVersion = nextVersion
      organizationPreferencesEtag = `"prefs-${nextVersion}-${randomSuffix}"`

      return new HttpResponse(null, {
        status: 200,
        headers: { ETag: organizationPreferencesEtag },
      })
    })
  )

const DEFAULT_INTEGRATIONS: Integration[] = [
  FakeGitHubIntegration(),
  FakeGitLabIntegration(),
  FakeAzureIntegration(),
  FakeBitbucketIntegration(),
  FakeCheckmarxIntegration(),
  FakeAppScanIntegration(),
  FakeSonarIntegration({ id: 'sonar-1', name: 'SonarCloud', source: 'managed' }),
  FakeDatadogIntegration({ id: 'datadog-1', name: 'Production' }),
  FakeDatadogIntegration({ id: 'datadog-2', name: 'Staging' }),
]

export const getIntegrations = ({
  handler,
  paginatedIntegrations,
}: { handler?: Handler; paginatedIntegrations?: IntegrationPaginatedResponse } = {}) =>
  http.get(
    '/api/v1/integrations',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            paginatedIntegrations ??
              FakeIntegrationPaginatedResponse({
                _embedded: {
                  items: [
                    FakeGitHubIntegration(),
                    FakeGitLabIntegration(),
                    FakeAzureIntegration(),
                    FakeBitbucketIntegration(),
                    FakeCheckmarxIntegration(),
                    FakeAppScanIntegration(),
                    // Three managed tool instances in the configured zone: one SonarQube plus two
                    // Datadog (the two-of-the-same-tool case). Only managed instances surface there.
                    FakeSonarIntegration({ id: 'sonar-1', name: 'SonarCloud', source: 'managed' }),
                    FakeDatadogIntegration({ id: 'datadog-1', name: 'Production' }),
                    FakeDatadogIntegration({ id: 'datadog-2', name: 'Staging' }),
                  ],
                },
              })
          ))
    )
  )

/**
 * The integrations collection returning exactly these items — the shape every test and story wants.
 *
 * Unlike a fixed `paginatedIntegrations` body, this honours `page-number`/`page-size`, so a caller
 * that walks pages sees the whole list and one that reads a single page sees only that page. Tests
 * about paging are meaningless against a handler that answers every request with the same array.
 *
 * `isPageFailing` answers 500 for the pages it returns true for, so a spec can fail one page of an
 * otherwise healthy list. It is an option here rather than a second handler because the runners
 * disagree on which of two handlers for one path wins: MSW tries the first registered,
 * playwright-msw the last.
 */
export const integrationsReturning = (
  items: Integration[],
  { isPageFailing = () => false }: { isPageFailing?: (pageNumber: number) => boolean } = {}
) =>
  http.get(
    '/api/v1/integrations',
    withAuth(({ request }) => {
      const url = new URL(request.url)
      const matching = items.filter(item => matchesIntegrationQuery(item, url.searchParams))
      const number = Number(url.searchParams.get('page-number') ?? 0)
      const size = Number(url.searchParams.get('page-size') ?? 10)
      if (isPageFailing(number)) {
        return HttpResponse.json({ title: 'Internal Server Error', status: 500 }, { status: 500 })
      }
      const start = number * size
      return HttpResponse.json(
        FakeIntegrationPaginatedResponse({
          total: matching.length,
          page: { number, size },
          _embedded: { items: matching.slice(start, start + size) },
        })
      )
    })
  )

/**
 * Applies `type`, `source` and `search` the way the platform does, so a client that relies on
 * server-side narrowing is tested against it rather than against a handler that returns everything
 * regardless.
 *
 * The search rule differs by source, and both halves are reproduced here:
 *
 * - **managed** instances are persisted rows, filtered in SQL by `ManagedIntegrationQueries.filter`:
 *   the id is matched whole because it is a UUID, where a substring match would be noise; name and
 *   base URI are matched as substrings.
 * - **system** instances are config-derived Spring beans, filtered in memory by
 *   `RegistryIntegrationsResource.matchesTerm`: id and name are matched as substrings — the id is a
 *   readable slug like `datadog-default` — and the base URI is never considered.
 */
const matchesIntegrationQuery = (integration: Integration, params: URLSearchParams): boolean => {
  const type = params.get('type')
  if (type && integration.type !== type) {
    return false
  }
  const source = params.get('source')
  if (source && integration.source !== source) {
    return false
  }
  const search = params.get('search')?.trim().toLowerCase()
  if (!search) {
    return true
  }
  const isManaged = integration.source === 'managed'
  const id = integration.id.toLowerCase()
  const matchesId = isManaged ? id === search : id.includes(search)
  const baseUri = 'base_uri' in integration ? (integration.base_uri ?? '') : ''
  const matchesBaseUri = isManaged && baseUri.toLowerCase().includes(search)
  return matchesId || integration.name.toLowerCase().includes(search) || matchesBaseUri
}

export const getIntegrationTypeCounts = ({
  handler,
  items,
  typeCounts,
}: { handler?: Handler; items?: Integration[]; typeCounts?: IntegrationTypeCountsResponse } = {}) =>
  http.get(
    '/api/v1/integration-types',
    withAuth(
      handler ??
        (() => HttpResponse.json(typeCounts ?? FakeIntegrationTypeCountsResponse(items ?? DEFAULT_INTEGRATIONS)))
    )
  )

export const getIntegration = ({
  handler,
  integration,
  items,
}: { handler?: Handler; integration?: Integration; items?: Integration[] } = {}) =>
  http.get(
    '/api/v1/integrations/:integrationId',
    withAuth(
      handler ??
        (({ params }) => {
          const match = integration ?? (items ?? DEFAULT_INTEGRATIONS).find(item => item.id === params.integrationId)
          return match ? HttpResponse.json(match) : new HttpResponse(null, { status: 404 })
        })
    )
  )

type IntegrationsReturningOptions = Parameters<typeof integrationsReturning>[1]

export const integrationsAndTypeCountsReturning = (items: Integration[], options?: IntegrationsReturningOptions) =>
  Object.values(integrationHandlers(items, options))

export const integrationHandlers = (items: Integration[], options?: IntegrationsReturningOptions) =>
  ({
    getIntegrations: integrationsReturning(items, options),
    getIntegrationTypeCounts: getIntegrationTypeCounts({ items }),
    getIntegration: getIntegration({ items }),
  }) satisfies Partial<typeof defaultHandlers>

// The methods an installation permits on the collection. Defaults to the full set so the mocked app
// and stories exercise in-product management; pass `allow` to mock a read-only installation.
export const optionsIntegrations = ({
  allow = 'GET, POST, OPTIONS',
  status = 204,
}: { allow?: string; status?: number } = {}) =>
  http.options('/api/v1/integrations', () => new HttpResponse(null, { status, headers: { Allow: allow } }))

// Default to success so the mocked app and stories show the drawers' success path.
//
// The create's body is read: the Setup drawer pulls the `webhooks` link out of it to hand a Sonar or
// AppScan customer the URL their tool must call. `SonarIntegrationLinks` and `AppScanIntegrationLinks`
// both require that link, so a default without one would send those two down the close-immediately
// path and leave the handoff unreachable in `dev:mocks` — the one surface that has no handler of its
// own to supply. It is sent for every type because this handler does not know which was posted, and
// the drawer only reads it for a tool that has a handoff. The close path and the handoff URL are
// still pinned by tests and stories that install their own handler. The update and delete bodies are
// unread, and stay minimal rather than inventing a contract nothing asserts against.
export const postIntegration = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/integrations',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            {
              id: 'new-integration',
              _links: {
                self: { href: '/api/v1/integrations/new-integration' },
                webhooks: { href: '/api/v1/integrations/new-integration/webhooks' },
              },
            },
            { status: 201 }
          ))
    )
  )

export const putIntegration = ({ handler }: { handler?: Handler } = {}) =>
  http.put('/api/v1/integrations/:integrationId', withAuth(handler ?? (() => HttpResponse.json({}, { status: 200 }))))

export const deleteIntegration = ({ handler }: { handler?: Handler } = {}) =>
  http.delete(
    '/api/v1/integrations/:integrationId',
    withAuth(handler ?? (() => new HttpResponse(null, { status: 204 })))
  )

export const getRepository = ({
  handler,
  repository,
  fallback,
}: { handler?: Handler; repository?: Repository; fallback?: Repository } = {}) =>
  http.get(
    '/api/v1/repositories/:repository_id',
    withAuth(
      handler ??
        (({ params, request }) => {
          if (repository) {
            return HttpResponse.json(repository)
          }
          return HttpResponse.json(
            resolveRepository({ id: String(params.repository_id), embed: wantsMembershipEmbed(request), fallback })
          )
        })
    )
  )

// Resolve the id against the seeded estate so this answers the same repository the list does,
// membership included. Serving an unrelated fake made the settings drawer disagree with the row that
// opened it, which the real API never does. An id the store has never heard of keeps its old answer, so
// callers that navigate to an invented id are unaffected.
const resolveRepository = ({ id, embed, fallback }: { id: string; embed: boolean; fallback?: Repository }) =>
  projectStore.repositories({ embed }).find(item => item.id === id) ?? fallback ?? FakeRepository({ id })

export const getRepositories = ({
  repositories,
  paginatedRepositories,
}: {
  repositories?: Partial<Repository>[]
  paginatedRepositories?: Partial<RepositoryPaginatedResponse>
} = {}) =>
  http.get(
    '/api/v1/repositories',
    withAuth(({ request }) => {
      if (paginatedRepositories) {
        return HttpResponse.json(paginatedRepositories)
      }

      const pageNumber = Number(new URL(request.url).searchParams.get('page-number')) || 0
      const pageSize = Number(new URL(request.url).searchParams.get('page-size')) || 10
      const nameFilter = new URL(request.url).searchParams.get('name') || ''

      const allItems = repositories ?? [
        FakeRepository({ type: 'github' }),
        FakeRepository({ type: 'gitlab' }),
        FakeRepository({ type: 'azure' }),
        ...Array(97)
          .fill(null)
          .map(() => FakeRepository()),
      ]

      const projectParams = new URL(request.url).searchParams.getAll('project')

      // Filter items by name if name parameter is provided
      const nameFilteredItems = nameFilter
        ? allItems.filter(item => item.name?.toLowerCase().includes(nameFilter.toLowerCase()))
        : allItems
      // Membership is the project store's: a repository fixture carries none of its own unless a test
      // sets one, so the store decides which rows a `project` filter keeps and what badge each shows.
      const filteredItems = projectStore.filterByProject(
        nameFilteredItems.map(item => ({ ...item, id: item.id ?? faker.string.uuid() })),
        projectParams
      )

      const total = filteredItems.length
      const items = filteredItems.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize)

      const paginatedRepositoriesResponse: RepositoryPaginatedResponse = FakePaginatedRepositories({
        total,
        page: { number: pageNumber, size: pageSize },
        _embedded: {
          // The store owns membership: it stamps the `project-membership` link (always) and the
          // embedded membership + project (when the row is grouped), the way the real API answers
          // `embed=items.project-membership.project`.
          items: items.map(item =>
            projectStore.stampMembership(
              FakeRepository({
                ...item,
                html_url: `https://github.com/${item.name}`,
              }),
              { embed: wantsMembershipEmbed(request) }
            )
          ),
        },
      })

      return HttpResponse.json(paginatedRepositoriesResponse)
    })
  )

export const getScan = ({
  handler,
  scan,
  repository,
}: { handler?: Handler; scan?: ScanResponse; repository?: Repository } = {}) =>
  http.get(
    '/api/v1/scans/:scanId',
    withAuth(
      handler ??
        (({ request }) => {
          const response = scan ?? FakeScan()
          const embeds = new URL(request.url).searchParams.getAll('embed')
          if (!embeds.includes(ScanEmbedParameterSchema.enum['repository.project-membership.project'])) {
            return HttpResponse.json(response)
          }
          const repositoryId = response._links.repository.href.split('/').at(-1)
          invariant(repositoryId, 'the scan should link to its repository')
          const embedded = repository ?? resolveRepository({ id: repositoryId, embed: true })
          return HttpResponse.json({ ...response, _embedded: { ...response._embedded, repository: embedded } })
        })
    )
  )

export const getScans = ({
  handler,
  items,
  paginatedScans,
  itemsByAnalysisState,
}:
  | { handler: Handler; items?: undefined; paginatedScans?: undefined; itemsByAnalysisState?: undefined }
  | {
      handler?: undefined
      items?: ScanResponse[]
      paginatedScans?: ScanAggregateResponse
      /** Map analysis-state query param values to scan arrays. When provided, overrides `items` for matching states. */
      itemsByAnalysisState?: Partial<Record<'completed' | 'in-progress' | 'not-analyzed', ScanResponse[]>>
    } = {}) =>
  http.get(
    '/api/v1/scans',
    withAuth(
      handler ??
        (async ({ request }) => {
          // If paginatedScans is provided, return it directly (backward compatibility)
          if (paginatedScans) {
            return HttpResponse.json(paginatedScans)
          }

          const url = new URL(request.url)
          const pageNumber = Number(url.searchParams.get('page-number')) || 0
          const pageSize = Number(url.searchParams.get('page-size')) || 10
          const tools = url.searchParams.getAll('tools')
          const repositoryIds = url.searchParams.getAll('repository-ids')
          const analysisState = url.searchParams.get('analysis-state') as
            'completed' | 'in-progress' | 'not-analyzed' | null

          // Select scans based on analysis-state filter
          const allScans: ScanResponse[] =
            analysisState && itemsByAnalysisState?.[analysisState] !== undefined
              ? itemsByAnalysisState[analysisState]
              : (items ?? [])

          // Filter by tools if provided
          let filteredScans = allScans
          if (tools.length > 0) {
            filteredScans = filteredScans.filter(scan => tools.includes(scan.detector))
          }

          // Filter by repository-ids if provided (extract ID from _links.repository.href)
          if (repositoryIds.length > 0) {
            filteredScans = filteredScans.filter(scan => {
              const repositoryHref = scan._links?.repository?.href
              if (!repositoryHref) return false
              const match = /\/repositories\/([^/]+)$/.exec(repositoryHref)
              return match?.[1] ? repositoryIds.includes(match[1]) : false
            })
          }

          const start = pageNumber * pageSize
          const paginatedScansItems = filteredScans.slice(start, start + pageSize)

          const response = FakePaginatedScans({
            _links: {
              first: {
                href: url.toString(),
              },
              self: {
                href: url.toString(),
              },
            },
            total: filteredScans.length,
            page: { number: pageNumber, size: pageSize },
            _embedded: {
              items: paginatedScansItems,
            },
          })

          return HttpResponse.json(response)
        })
    )
  )

/**
 * `GET /api/v1/repositories/:repository_id/branches`, filtered, ordered and paged the way the platform
 * does. `itemsByTool` answers per tool; `items` answers for any tool without its own list, and when the
 * request names no tool. Either way the answer is ordered newest import first (by each branch's
 * embedded latest scan), then by name, whatever order the caller gave. Branches keep their embedded
 * latest scan only when the request asks for `embed=latest-scan`.
 */
export const getRepositoryBranches = ({
  handler,
  items,
  itemsByTool,
}: {
  handler?: Handler
  items?: BranchResponse[]
  itemsByTool?: Partial<Record<Tool, BranchResponse[]>>
} = {}) =>
  http.get(
    '/api/v1/repositories/:repository_id/branches',
    withAuth(
      handler ??
        (({ request }) => {
          const url = new URL(request.url)
          const pageNumber = Number(url.searchParams.get('page-number')) || 0
          const pageSize = Number(url.searchParams.get('page-size')) || 20
          const tool = ToolSchema.safeParse(url.searchParams.get('tool')).data
          const search = url.searchParams.get('search')?.toLowerCase()
          const names = url.searchParams.getAll('name')
          const embedsLatestScan = url.searchParams.getAll('embed').includes('latest-scan')

          const importedAt = (branch: BranchResponse) => branch._embedded?.['latest-scan']?.imported_at ?? ''
          const branches = ((tool ? itemsByTool?.[tool] : undefined) ?? items ?? [])
            .filter(
              branch =>
                (!search || branch.name.toLowerCase().includes(search)) &&
                (names.length === 0 || names.includes(branch.name))
            )
            .sort(
              (left, right) => importedAt(right).localeCompare(importedAt(left)) || left.name.localeCompare(right.name)
            )
            .map(branch => (embedsLatestScan ? branch : { ...branch, _embedded: undefined }))

          return HttpResponse.json(
            FakePaginatedBranches({
              total: branches.length,
              page: { number: pageNumber, size: pageSize },
              _links: { first: { href: url.toString() }, self: { href: url.toString() } },
              _embedded: { items: branches.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize) },
            })
          )
        })
    )
  )

export const getScanAnalysis = ({
  scanAnalysis,
  handler,
}: { scanAnalysis?: RecursivePartial<AnalysisResponse>; handler?: Handler } = {}) =>
  http.get(
    '/api/v1/analyses/:analysisId',
    withAuth(
      handler ??
        (({ params }) => {
          const { analysisId } = params
          return HttpResponse.json(FakeScanAnalysis(scanAnalysis ?? { id: analysisId as string }))
        })
    )
  )

export const getFindingArticle = ({ handler, article }: { handler?: Handler; article?: string } = {}) =>
  http.get(
    '/api/v1/analyses/:analysisId/findings/:findingId/article',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(article ?? exampleFindingTriageArticle, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          }))
    )
  )

export const getTriageResultArticle = ({ handler, article }: { handler?: Handler; article?: string } = {}) =>
  http.get(
    '/api/v1/triage-results/:resultId/article',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(article ?? exampleFindingTriageArticle, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          }))
    )
  )

export const getScaResultArticle = ({ handler, article }: { handler?: Handler; article?: string } = {}) =>
  http.get(
    '/api/v1/sca-results/:resultId/article',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(article ?? exampleScaArticle, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          }))
    )
  )

export const getScaResultReport = ({ handler, report }: { handler?: Handler; report?: string } = {}) =>
  http.get(
    '/api/v1/sca-results/:resultId/report',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(report ?? exampleScaReport, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          }))
    )
  )

export const getAnalysisPreferences = ({ handler, memory }: { handler?: Handler; memory?: string } = {}) =>
  http.get(
    '/api/v1/analyses/:analysisId/preferences',
    withAuth(
      handler ??
        (() => {
          if (!memory) {
            return new HttpResponse(null, { status: 204 })
          }
          return new HttpResponse(memory, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          })
        })
    )
  )

export const getFindingReport = ({ handler, report }: { handler?: Handler; report?: string } = {}) =>
  http.get(
    '/api/v1/analyses/:analysisId/findings/:findingId/report',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(report ?? exampleFindingTriageReport, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          }))
    )
  )

export const getTriageResultReport = ({ handler, report }: { handler?: Handler; report?: string } = {}) =>
  http.get(
    '/api/v1/triage-results/:resultId/report',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(report ?? exampleFindingTriageReport, {
            headers: { 'Content-Type': 'text/markdown; variant=GFM' },
          }))
    )
  )

export const getTriageResultVerification = ({
  handler,
  verification,
}: { handler?: Handler; verification?: ResultsVerificationResponse } = {}) =>
  http.get(
    '/api/v1/triage-results/:resultId/verification',
    withAuth(handler ?? (() => HttpResponse.json(verification ?? FakeResultsVerificationResponse())))
  )

const DEFAULT_CSV_EXPORT = `id,title,severity,severity_score,suggested_severity,suggested_severity_score,triage_status,triage_suggested_status,fix_status,fix_confidence,sca_status,sca_classification,file_path,line_number,detector,rule_id,html_url
finding-1,SQL Injection Vulnerability,HIGH,8.5,CRITICAL,9.2,completed,true_positive,completed,high,,,"src/main/java/App.java",42,codeql,java/sql-injection,https://example.com/finding/1
finding-2,Cross-Site Scripting (XSS),MEDIUM,6.0,HIGH,7.5,completed,suspicious,in_progress,medium,,,"src/main/java/Controller.java",128,codeql,java/xss,https://example.com/finding/2
finding-3,Hardcoded Credentials,HIGH,8.0,HIGH,8.0,pending,,,,,,"src/main/java/Config.java",15,codeql,java/hardcoded-credential,https://example.com/finding/3
finding-4,Path Traversal,MEDIUM,5.5,MEDIUM,5.5,completed,false_positive,not_applicable,,,,"src/main/java/FileHandler.java",67,codeql,java/path-injection,https://example.com/finding/4
finding-5,Vulnerable Dependency,HIGH,7.8,HIGH,7.8,completed,,completed,,COMPLETED,exploitable,"pom.xml",23,snyk,SNYK-JAVA-LOG4J,https://example.com/finding/5
`

const DEFAULT_FINDINGS = SAST_FINDINGS
// or
// const DEFAULT_FINDINGS = SCA_FINDINGS

export const getScanFindings = ({
  handler,
  paginatedFindingsWithResults,
  analyzedFindings,
  csvExport,
  withFilters = false,
}: {
  handler?: Handler
  paginatedFindingsWithResults?: FindingAggregateResponse
  // Served for `analyzed=true` requests, whose `total` is the end-to-end analyzed count
  analyzedFindings?: FindingAggregateResponse
  csvExport?: string
  withFilters?: boolean
} = {}) =>
  http.get(
    '/api/v1/scans/:scanId/findings',
    withAuth(
      handler ??
        (async ({ request }) => {
          const acceptHeader = request.headers.get('Accept')
          if (acceptHeader === 'text/csv') {
            await new Promise(resolve => setTimeout(resolve, 2000))
            return new HttpResponse(csvExport ?? DEFAULT_CSV_EXPORT, {
              status: 200,
              headers: {
                'Content-Type': 'text/csv',
              },
            })
          }

          const url = new URL(request.url)

          if (analyzedFindings && url.searchParams.get(SCAN_FINDINGS_PARAMS.analyzed) === 'true') {
            return HttpResponse.json(analyzedFindings)
          }

          if (paginatedFindingsWithResults && !withFilters) {
            return HttpResponse.json(paginatedFindingsWithResults)
          }

          const mockFindings = paginatedFindingsWithResults
            ? paginatedFindingsWithResults._embedded.items.map(fromFindingResponse)
            : DEFAULT_FINDINGS

          return HttpResponse.json(paginateAndFilter(mockFindings, url.searchParams))
        })
    )
  )

export const getChangesetsForFix = ({
  handler,
  paginatedChangesets,
}: {
  handler?: Handler
  paginatedChangesets?: Partial<ChangesetPaginatedResponse>
} = {}) =>
  http.get(
    '/api/v1/fixes/:fixId/changesets',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json({
            _links: {
              first: {
                href: '/api/v1/fixes/:fixId/changesets',
              },
              self: {
                href: '/api/v1/fixes/:fixId/changesets',
              },
            },
            total: paginatedChangesets?.total ?? 1,
            page: paginatedChangesets?.page ?? {
              number: 0,
              size: 10,
            },
            _embedded: {
              items: paginatedChangesets?._embedded?.items ?? [
                {
                  path: 'src/main/java/io/github/pixee/security/XmlProcessor.java',
                  preview:
                    ' package io.github.pixee.security;\n \n import javax.xml.parsers.DocumentBuilderFactory;\n+import javax.xml.parsers.ParserConfigurationException;\n import org.w3c.dom.Document;\n \n public class XmlProcessor {\n \n-    public Document parse(String xml) {\n-        DocumentBuilderFactory factory = DocumentBuilderFactory.newInstance();\n+    public Document parse(String xml) throws ParserConfigurationException {\n+        DocumentBuilderFactory factory = DocumentBuilderFactory.newInstance();\n+        factory.setFeature("http://apache.org/xml/features/disallow-doctype-decl", true);\n+        factory.setFeature("http://xml.org/sax/features/external-general-entities", false);\n         return factory.newDocumentBuilder().parse(xml);\n     }\n }\n',
                },
              ],
            },
          } satisfies ChangesetPaginatedResponse))
    )
  )

export const getFixResultChangesets = ({
  handler,
  paginatedChangesets,
}: {
  handler?: Handler
  paginatedChangesets?: Partial<ChangesetPaginatedResponse>
} = {}) =>
  http.get(
    '/api/v1/fix-results/:resultId/changesets',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json({
            _links: {
              first: {
                href: '/api/v1/fix-results/:resultId/changesets',
              },
              self: {
                href: '/api/v1/fix-results/:resultId/changesets',
              },
            },
            total: paginatedChangesets?.total ?? 1,
            page: paginatedChangesets?.page ?? {
              number: 0,
              size: 10,
            },
            _embedded: {
              items: paginatedChangesets?._embedded?.items ?? [
                {
                  path: 'src/main/java/io/github/pixee/security/SqlHandler.java',
                  preview:
                    ' package io.github.pixee.security;\n \n import java.sql.Connection;\n import java.sql.PreparedStatement;\n import java.sql.ResultSet;\n-import java.sql.Statement;\n import javax.servlet.http.HttpServletRequest;\n \n public class SqlHandler {\n \n     public ResultSet getUser(Connection conn, HttpServletRequest req) throws Exception {\n         String userId = req.getParameter("id");\n-        Statement stmt = conn.createStatement();\n-        return stmt.executeQuery("SELECT * FROM users WHERE id = \'" + userId + "\'");\n+        PreparedStatement stmt = conn.prepareStatement("SELECT * FROM users WHERE id = ?");\n+        stmt.setString(1, userId);\n+        return stmt.executeQuery();\n     }\n }\n',
                },
              ],
            },
          } satisfies ChangesetPaginatedResponse))
    )
  )

export const getFixResultPatches = ({
  handler,
  patches,
}: {
  handler?: Handler
  patches?: Patch[]
} = {}) =>
  http.get(
    '/api/v1/fix-results/:resultId/patches',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json({
            _links: {
              first: {
                href: '/api/v1/fix-results/:resultId/patches',
              },
              self: {
                href: '/api/v1/fix-results/:resultId/patches',
              },
            },
            total: patches?.length ?? 0,
            page: {
              number: 0,
              size: 10,
            },
            _embedded: {
              items: patches ?? [],
            },
          } satisfies PatchPaginatedResponse))
    )
  )

export const getAzureOrganizations = ({ handler, items }: { handler?: Handler; items?: AzureOrganization[] } = {}) =>
  http.get(
    '/api/v1/integrations/:integrationId/organizations',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json({
            _links: {
              first: {
                href: '/api/v1/integrations/azure-default/organizations',
              },
              self: {
                href: '/api/v1/integrations/azure-default/organizations',
              },
            },
            _embedded: {
              items: items ?? [{ name: 'pixee' }, { name: 'other-org' }],
            },
          } satisfies AzureOrganizationPaginatedResponse))
    )
  )

export const getAzureProjects = ({ handler, items }: { handler?: Handler; items?: AzureProject[] } = {}) => {
  const projects = items ?? [
    { id: 'project1', name: 'WebGoats' },
    { id: 'project2', name: 'Other Project' },
  ]
  return http.get(
    '/api/v1/integrations/:integrationId/:organization/projects',
    withAuth(
      handler ??
        (({ request }) =>
          respondWithPage(
            pageOfCaseInsensitiveMatches(
              request,
              projects,
              project => project.name
            ) satisfies PickerPageAnswer<AzureProjectPaginatedResponse>
          ))
    )
  )
}

export const getAzureRepositories = ({
  handler,
  items,
}: { handler?: Handler; items?: AzureIntegrationRepository[] } = {}) => {
  const repositories = items ?? [
    {
      id: 'repo1',
      name: 'Repository 1',
      project: { id: 'project1', name: 'WebGoats' },
    },
    {
      id: 'repo2',
      name: 'Connected Repository',
      pixee_id: 'already-connected',
      project: { id: 'project1', name: 'WebGoats' },
    },
  ]
  return http.get(
    '/api/v1/integrations/:integrationId/:organization/projects/:projectId/repositories',
    withAuth(
      handler ??
        (({ request }) =>
          respondWithPage(
            pageOfCaseInsensitiveMatches(
              request,
              repositories,
              repository => repository.name
            ) satisfies PickerPageAnswer<AzureRepositoryPaginatedResponse>
          ))
    )
  )
}

export const getBitbucketWorkspaces = ({
  handler,
  items,
}: { handler?: Handler; items?: BitbucketWorkspace[] } = {}) => {
  const workspaces = items ?? Array.from({ length: 3 }, () => FakeBitbucketWorkspace())
  return http.get(
    '/api/v1/integrations/:integrationId/workspaces',
    withAuth(
      handler ??
        (({ request }) =>
          respondWithPage(
            pageOfCaseInsensitiveMatches(
              request,
              workspaces,
              workspace => workspace.slug
            ) satisfies PickerPageAnswer<BitbucketWorkspacePaginatedResponse>
          ))
    )
  )
}

export const getBitbucketRepositories = ({
  handler,
  items,
}: { handler?: Handler; items?: IntegrationsBitbucketRepositoryResponse[] } = {}) => {
  const repositories = items ?? Array.from({ length: 5 }, () => FakeBitbucketIntegrationRepository())
  return http.get(
    '/api/v1/integrations/:integrationId/workspaces/:workspaceSlug/repositories',
    withAuth(
      handler ??
        (({ request }) =>
          respondWithPage(
            pageOfCaseInsensitiveMatches(
              request,
              repositories,
              repository => repository.name
            ) satisfies PickerPageAnswer<BitbucketRepositoryPaginatedResponse>
          ))
    )
  )
}

export const getAnalyses = ({ handler, items }: { handler?: Handler; items?: AnalysisResponse[] } = {}) =>
  http.get(
    '/api/v1/analyses',
    withAuth(
      handler ??
        (async ({ request }) => {
          const url = new URL(request.url)
          const pageNumber = Number(url.searchParams.get('page-number')) || 0
          const pageSize = Number(url.searchParams.get('page-size')) || 10
          const states = url.searchParams.getAll('states') as AnalysisStateResponse[]
          const tools = url.searchParams.getAll('tools')

          const allAnalyses = items ?? []

          let filteredAnalyses = allAnalyses

          // Filter by states if provided
          if (states.length > 0) {
            filteredAnalyses = filteredAnalyses.filter(analysis => states.includes(analysis.current_state.state))
          }

          // Filter by tools if provided
          if (tools.length > 0) {
            filteredAnalyses = filteredAnalyses.filter(analysis => {
              const detector = analysis._embedded?.scan?.detector
              return detector !== undefined && tools.includes(detector)
            })
          }

          const start = pageNumber * pageSize
          const paginatedAnalyses = filteredAnalyses.slice(start, start + pageSize)

          const response: AnalysisPaginatedResponse = {
            _links: {
              first: {
                href: url.toString(),
              },
              self: {
                href: url.toString(),
              },
            },
            total: filteredAnalyses.length,
            page: { number: pageNumber, size: pageSize },
            _embedded: {
              items: paginatedAnalyses,
            },
          }

          return HttpResponse.json(response)
        })
    )
  )

export const getWorkflows = ({
  handler,
  items,
}: {
  handler?: Handler
  items?: WorkflowResponse[]
} = {}) =>
  http.get(
    '/api/v1/repositories/:repository_id/workflows',
    withAuth(
      handler ??
        (({ params }) => {
          const defaultWorkflows: WorkflowResponse[] = [
            {
              _links: {
                self: { href: '/api/v1/workflows/workflow-1' },
                repository: { href: `/api/v1/repositories/${params.repository_id}` },
                scale: { href: getScaleHrefForTool('semgrep') },
              },
              id: 'workflow-1',
              name: 'Daily Semgrep fixes',
              enabled: true,
              tool: 'semgrep' as const,
              created_at: '2025-02-01T12:00:00Z',
              event: { type: 'schedule' as const, cadence: 'daily' as const },
              action: { type: 'none' as const },
            },
            {
              _links: {
                self: { href: '/api/v1/workflows/workflow-2' },
                repository: { href: `/api/v1/repositories/${params.repository_id}` },
                scale: { href: getScaleHrefForTool('codeql') },
              },
              id: 'workflow-2',
              name: 'Weekly CodeQL scan',
              enabled: true,
              tool: 'codeql' as const,
              created_at: '2025-02-03T12:00:00Z',
              event: { type: 'schedule' as const, cadence: 'weekly' as const },
              action: { type: 'none' as const },
            },
            {
              _links: {
                self: { href: '/api/v1/workflows/workflow-3' },
                repository: { href: `/api/v1/repositories/${params.repository_id}` },
                scale: { href: getScaleHrefForTool('sonar') },
              },
              id: 'workflow-3',
              name: 'Sonar PR scan',
              enabled: true,
              tool: 'sonar' as const,
              created_at: '2025-02-05T12:00:00Z',
              event: { type: 'pull-request-scan' as const, target_branch: 'main' },
              action: { type: 'none' as const },
            },
            {
              _links: {
                self: { href: '/api/v1/workflows/workflow-4' },
                repository: { href: `/api/v1/repositories/${params.repository_id}` },
                scale: { href: getScaleHrefForTool('snyk') },
              },
              id: 'workflow-4',
              name: 'Snyk scan and fix',
              enabled: true,
              tool: 'snyk' as const,
              created_at: '2025-02-07T12:00:00Z',
              event: { type: 'new-scan' as const, branch: 'main' },
              action: {
                type: 'create-patch' as const,
                findings: { include_severity_labels: ['critical', 'high'] },
                fixes: { minimum_fix_confidence: 'medium' as const },
              },
            },
            {
              _links: {
                self: { href: '/api/v1/workflows/workflow-5' },
                repository: { href: `/api/v1/repositories/${params.repository_id}` },
                scale: { href: getScaleHrefForTool('checkmarx') },
              },
              id: 'workflow-5',
              name: 'Checkmarx analyze only',
              enabled: false,
              tool: 'checkmarx' as const,
              created_at: '2025-02-09T12:00:00Z',
              event: { type: 'new-scan' as const, branch: 'main' },
              action: { type: 'none' as const },
            },
            {
              _links: {
                self: { href: '/api/v1/workflows/workflow-3' },
                repository: { href: `/api/v1/repositories/${params.repository_id}` },
                scale: { href: getScaleHrefForTool('snyk') },
              },
              id: 'workflow-3',
              name: 'Disabled Snyk PR scan',
              enabled: false,
              tool: 'snyk',
              created_at: '2025-02-11T12:00:00Z',
              event: { type: 'pull-request-scan', target_branch: 'main' },
              action: { type: 'none' },
            },
          ]

          const allWorkflows = items ?? defaultWorkflows

          return HttpResponse.json({
            _links: {
              first: { href: `/api/v1/repositories/${params.repository_id}/workflows` },
              self: { href: `/api/v1/repositories/${params.repository_id}/workflows` },
            },
            total: allWorkflows.length,
            page: { number: 0, size: 10 },
            _embedded: {
              items: allWorkflows,
            },
          })
        })
    )
  )

export const getScale = ({ handler, scale }: { handler?: Handler; scale?: Scale } = {}) =>
  http.get(
    '/api/v1/scales/:scaleId',
    withAuth(
      handler ??
        (({ params }) => {
          return HttpResponse.json(
            scale ??
              FakeScale({
                id: params.scaleId as string,
              })
          )
        })
    )
  )

const getScales = http.get('/api/v1/scales', ({ request }) => {
  const tool = ToolSchema.safeParse(new URL(request.url).searchParams.get('tool'))
  const items = tool.success ? [FakeScale({ id: getScaleIdForTool(tool.data) })] : []
  return HttpResponse.json(FakePaginatedScales({ total: items.length, _embedded: { items } }))
})

const getCodeqlScale = http.get('/api/v1/codeql/scale', () => {
  return HttpResponse.json(
    FakeScale({
      id: 'codeql',
    })
  )
})

export const getScanFinding = ({
  handler,
  finding,
  embeddedResults,
}: {
  handler?: Handler
  finding?: FindingResponse
  embeddedResults?: FindingResultResponse[]
} = {}) =>
  http.get(
    '/api/v1/scans/:scan_id/findings/:finding_id',
    withAuth(
      handler ??
        (({ params, request }) => {
          invariantType(params.finding_id, 'string', 'finding_id')

          const url = new URL(request.url)
          const embed = url.searchParams.get('embed')

          if (finding) {
            // If embed=results and embeddedResults is provided, add them to the finding
            if (embed === 'results' && embeddedResults) {
              return HttpResponse.json({
                ...finding,
                _embedded: {
                  ...finding._embedded,
                  results: {
                    _links: {
                      first: { href: `/api/v1/scans/scan-id/findings/${params.finding_id}/results` },
                      self: { href: `/api/v1/scans/scan-id/findings/${params.finding_id}/results` },
                      finding: { href: '#' },
                    },
                    _embedded: { items: embeddedResults },
                    page: { number: 0, size: 100 },
                    total: embeddedResults.length,
                  },
                },
              })
            }
            return HttpResponse.json(finding)
          }
          return HttpResponse.json(generateFindingWithMultipleResults(params.finding_id))
        })
    )
  )

type GetScanFindingResultsParams =
  | { handler?: Handler; results?: undefined; representativeResults?: undefined; allResults?: undefined }
  | { handler?: Handler; results: FindingResultResponse[]; representativeResults?: undefined; allResults?: undefined }
  | {
      handler?: Handler
      results?: undefined
      representativeResults: FindingResultResponse[]
      allResults: FindingResultResponse[]
    }

export const getScanFindingResults = ({
  handler,
  results,
  representativeResults,
  allResults,
}: GetScanFindingResultsParams = {}) =>
  http.get(
    '/api/v1/scans/:scan_id/findings/:finding_id/results',
    withAuth(
      handler ??
        (({ params, request }) => {
          invariantType(params.finding_id, 'string', 'finding_id')

          const url = new URL(request.url)
          const representative = url.searchParams.get('representative')

          if (representativeResults !== undefined) {
            // The type system guarantees that if representativeResults is defined, allResults is also defined.
            const items = representative === 'true' ? representativeResults : allResults
            return HttpResponse.json({
              _links: {
                first: { href: `/api/v1/scans/scan-id/findings/${params.finding_id}/results` },
                self: { href: `/api/v1/scans/scan-id/findings/${params.finding_id}/results` },
                finding: { href: '#' },
              },
              _embedded: { items },
              page: { number: 0, size: 100 },
              total: items.length,
            })
          }

          if (results) {
            return HttpResponse.json({
              _links: {
                first: { href: `/api/v1/scans/scan-id/findings/${params.finding_id}/results` },
                self: { href: `/api/v1/scans/scan-id/findings/${params.finding_id}/results` },
                finding: { href: '#' },
              },
              _embedded: { items: results },
              page: { number: 0, size: 100 },
              total: results.length,
            })
          }
          return HttpResponse.json(generateFullResultsForFinding(params.finding_id))
        })
    )
  )

export const getGitLabProjects = ({
  handler,
  items,
  itemsByIntegrationId,
}: {
  handler?: Handler
  items?: GitLabProjectResponse[]
  /** Projects per connection, for asserting that each connection lists its own. */
  itemsByIntegrationId?: Record<string, GitLabProjectResponse[]>
} = {}) =>
  http.get(
    // Templated rather than the literal `gitlab-default`, because an installation can hold several
    // GitLab connections and each lists its own projects.
    '/api/v1/integrations/:integrationId/projects',
    withAuth(
      handler ??
        (({ request, params }) =>
          respondWithPage(
            pageOfCaseInsensitiveMatches(
              request,
              itemsByIntegrationId?.[String(params.integrationId)] ?? items ?? [],
              project => `${project.name_with_namespace} ${project.path}`
            ) satisfies PickerPageAnswer<GitLabProjectPaginatedResponse>
          ))
    )
  )

// The platform's `SonarComponentsResource.MINIMUM_SEARCH_LENGTH`: a shorter term lists every project.
const SONAR_MINIMUM_SEARCH_LENGTH = 2

export const getSonarProjects = ({
  handler,
  items,
  itemsByIntegrationId,
}: {
  handler?: Handler
  items?: SonarProjectResponse[]
  itemsByIntegrationId?: Record<string, SonarProjectResponse[]>
} = {}) =>
  http.get(
    '/api/v1/integrations/:integrationId/sonar-projects',
    withAuth(
      handler ??
        (({ request, params }) => {
          // As SonarQube Cloud does, verified against it for ISS-9283: a case-insensitive substring
          // match on the project's name, never its key.
          const search = new URL(request.url).searchParams.get('search')?.trim().toLowerCase() ?? ''
          const projects = itemsByIntegrationId?.[String(params.integrationId)] ?? items ?? []
          const matching =
            search.length < SONAR_MINIMUM_SEARCH_LENGTH
              ? projects
              : projects.filter(project => project.name.toLowerCase().includes(search))

          return respondWithPage(pageOf(request, matching) satisfies PickerPageAnswer<SonarProjectPaginatedResponse>)
        })
    )
  )

export const getRepositoryIntegrations = ({
  handler,
  paginatedIntegrations,
}: {
  handler?: Handler
  paginatedIntegrations?: RepositoryIntegrationPaginatedResponse
} = {}) =>
  http.get(
    '/api/v1/repositories/:repository_id/integrations',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            paginatedIntegrations ??
              FakePaginatedRepositoryIntegrations({
                _embedded: {
                  items: [
                    FakeRepositoryIntegration({ type: 'sonar' }),
                    FakeRepositoryIntegration({ type: 'checkmarx' }),
                    FakeRepositoryIntegration({ type: 'veracode' }),
                    FakeRepositoryIntegration({ type: 'appscan' }),
                    FakeRepositoryIntegration({ type: 'arnica' }),
                  ],
                },
              })
          ))
    )
  )

// =============================================
// Mutations
// =============================================

// Expectations for the multipart `metadata` JSON sent with `/scans` and `/sboms`.
// Present keys are asserted; an explicit `undefined` value means the key must be absent.
// (This is the documented `Object.hasOwn`-style edge case in `prefer-undefined-for-internal-state`.)
type ScanMetadataExpectations = {
  tool?: string | undefined
  sha?: string | undefined
  branch?: string | undefined
  gitlab_pipeline_id?: number | undefined
  integration_id?: string | undefined
}

const badRequest = (message: string) =>
  new HttpResponse(JSON.stringify({ message: `Bad Request: ${message}` }), {
    status: 400,
    headers: { 'Content-Type': 'application/json' },
  })

const validateMetadata = (
  parsed: unknown,
  expected: ScanMetadataExpectations
): HttpResponse<DefaultBodyType> | undefined => {
  if (typeof parsed !== 'object' || parsed === null) {
    console.error('metadata is not an object:', parsed)
    return badRequest('metadata must be an object')
  }
  const metadata = parsed as Record<string, unknown>
  for (const [key, expectedValue] of Object.entries(expected)) {
    const actual = metadata[key]
    if (actual !== expectedValue) {
      console.error(
        `metadata.${key} mismatch — expected ${JSON.stringify(expectedValue)}, got ${JSON.stringify(actual)}`
      )
      return badRequest(`metadata.${key} mismatch`)
    }
  }
  return undefined
}

type PostUploadScanOptions = {
  // Full custom handler. When set, the validation/response options below are ignored.
  handler?: Handler
  // Validations applied by the default handler.
  expectRepositoryId?: string
  expectMetadata?: ScanMetadataExpectations
  expectFileName?: string
  // Fires after a successful validation pass; useful for `was-this-called?` assertions.
  onCall?: () => void
  // Response options.
  scanId?: string
  delayMs?: number
}

export const postUploadScan = (options: PostUploadScanOptions = {}) =>
  http.post('/api/v1/repositories/:repositoryId/scans', withAuth(options.handler ?? buildDefaultScanHandler(options)))

const buildDefaultScanHandler =
  ({ expectRepositoryId, expectMetadata, expectFileName, onCall, scanId, delayMs }: PostUploadScanOptions): Handler =>
  async ({ request, params }) => {
    if (expectRepositoryId !== undefined && params.repositoryId !== expectRepositoryId) {
      return badRequest('Invalid repository ID')
    }
    if (expectMetadata !== undefined || expectFileName !== undefined) {
      try {
        const formData = await request.formData()
        if (expectMetadata !== undefined) {
          const metadataBlob = formData.get('metadata')
          if (!(metadataBlob instanceof Blob)) {
            console.error('scan metadata is not a Blob:', metadataBlob)
            return badRequest('metadata is required')
          }
          const validationError = validateMetadata(JSON.parse(await metadataBlob.text()), expectMetadata)
          if (validationError) {
            return validationError
          }
        }
        if (expectFileName !== undefined) {
          const file = formData.get('files')
          if (!(file instanceof File) || file.name !== expectFileName) {
            console.error(`expected file named "${expectFileName}", got:`, file)
            return badRequest('file mismatch')
          }
        }
      } catch (error) {
        console.log('postUploadScan: could not parse FormData', error)
        return badRequest('could not parse FormData')
      }
    }
    onCall?.()
    if (delayMs !== undefined) {
      await new Promise(resolve => setTimeout(resolve, delayMs))
    }
    return new HttpResponse(
      JSON.stringify(scanId === undefined ? FakeCreateScanResponse() : FakeCreateScanResponse({ id: scanId })),
      {
        status: 201,
        headers: { 'Content-Type': 'application/json' },
      }
    )
  }

type PostUploadSbomOptions = {
  handler?: Handler
  expectRepositoryId?: string
  expectMetadata?: ScanMetadataExpectations
  expectSbomFileName?: string
  onCall?: () => void
}

const fakeSbomResponse = () => ({
  _links: {
    self: { href: `/api/v1/sboms/${faker.string.uuid()}` },
    repository: { href: `/api/v1/repositories/${faker.string.uuid()}` },
  },
  id: faker.string.uuid(),
  sha: faker.git.commitSha(),
  branch: 'main',
  created_at: new Date().toISOString(),
})

export const postUploadSbom = (options: PostUploadSbomOptions = {}) =>
  http.post('/api/v1/repositories/:repositoryId/sboms', withAuth(options.handler ?? buildDefaultSbomHandler(options)))

const buildDefaultSbomHandler =
  ({ expectRepositoryId, expectMetadata, expectSbomFileName, onCall }: PostUploadSbomOptions): Handler =>
  async ({ request, params }) => {
    if (expectRepositoryId !== undefined && params.repositoryId !== expectRepositoryId) {
      return badRequest('Invalid repository ID')
    }
    if (expectMetadata !== undefined || expectSbomFileName !== undefined) {
      try {
        const formData = await request.formData()
        if (expectMetadata !== undefined) {
          const metadataBlob = formData.get('metadata')
          if (!(metadataBlob instanceof Blob)) {
            console.error('sbom metadata is not a Blob:', metadataBlob)
            return badRequest('metadata is required')
          }
          const validationError = validateMetadata(JSON.parse(await metadataBlob.text()), expectMetadata)
          if (validationError) {
            return validationError
          }
        }
        if (expectSbomFileName !== undefined) {
          const file = formData.get('file')
          if (!(file instanceof File) || file.name !== expectSbomFileName) {
            console.error(`expected sbom file named "${expectSbomFileName}", got:`, file)
            return badRequest('sbom file mismatch')
          }
        }
      } catch (error) {
        console.log('postUploadSbom: could not parse FormData', error)
        return badRequest('could not parse FormData')
      }
    }
    onCall?.()
    return new HttpResponse(JSON.stringify(fakeSbomResponse()), {
      status: 201,
      headers: { 'Content-Type': 'application/json' },
    })
  }

export const postAnalyzeScan = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/scans/:scanId/analyses',
    withAuth(
      handler ??
        (async ({ params }) => {
          const scanId = params.scanId
          if (!scanId) {
            return new HttpResponse(JSON.stringify({ message: 'Bad Request: scanId is required' }), { status: 400 })
          }
          await new Promise(resolve => setTimeout(resolve, 1000))
          return new HttpResponse(JSON.stringify(FakeScanAnalysis()), {
            status: 201,
            headers: {
              'Content-Type': 'application/json',
            },
          })
        })
    )
  )

export const deleteAnalysis = ({ handler }: { handler?: Handler } = {}) =>
  http.delete('/api/v1/analyses/:analysisId', withAuth(handler ?? (() => new HttpResponse(null, { status: 204 }))))

export const postAddRepository = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/repositories',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(JSON.stringify(FakeRepository({ type: 'git' })), {
            status: 201,
            headers: { 'Content-Type': 'application/json' },
          }))
    )
  )

export const postPatch = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/analyses/:analysisId/patches',
    withAuth(
      handler ??
        (async () => {
          await new Promise(resolve => setTimeout(resolve, 1000))
          return new HttpResponse(null, { status: 201, headers: { Location: '/api/v1/patches/mock-patch-id' } })
        })
    )
  )

export const postScanPatch = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/patches',
    withAuth(
      handler ??
        (async () => {
          await new Promise(resolve => setTimeout(resolve, 1000))
          return new HttpResponse(null, { status: 201, headers: { Location: '/api/v1/patches/mock-scan-patch-id' } })
        })
    )
  )

export const postCreateWorkflow = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/repositories/:repository_id/workflows',
    withAuth(
      handler ??
        (async () => {
          return HttpResponse.json(
            {
              type: 'fix_schedule',
              _links: {
                self: {
                  href: '/api/v1/workflows/abcd',
                },
                repository: {
                  href: '/api/v1/repositories/936cc92b-a783-403a-80ef-d679b9cef66d',
                },
              },
              id: 'abcd',
              name: 'The workflow name',
            } satisfies CreateWorkflowResponse,
            { status: 201 }
          )
        })
    )
  )

export const postRepositoryIntegration = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    // Same param name as `getRepositoryIntegrations`: playwright-msw routes by path string, so a
    // differently spelled twin becomes a second route for the same URL, and the GET route shadows
    // this one and sends the POST to the network unmocked.
    '/api/v1/repositories/:repository_id/integrations',
    withAuth(
      handler ??
        (() =>
          new HttpResponse(JSON.stringify(FakeRepositoryIntegration()), {
            status: 201,
            headers: { 'Content-Type': 'application/json' },
          }))
    )
  )

const pushesById = new Map<string, PushResponse>()

export const postPush = ({
  handler,
  createResponse,
}: { handler?: Handler; createResponse?: CreatePushResponse } = {}) =>
  http.post(
    '/api/v1/pushes',
    withAuth(
      handler ??
        (async ({ request }) => {
          const body = (await request.json().catch(() => ({}))) as {
            triage_result_ids?: string[]
            scan_id?: string
          }
          const triageResultIds = body.triage_result_ids ?? []
          const generatedPushes: PushResponse[] = triageResultIds.map(triageResultId => {
            const now = new Date().toISOString()
            return {
              id: createId(),
              triage_result_ids: [triageResultId],
              status: 'IN_PROGRESS',
              created_at: now,
              updated_at: now,
            }
          })
          const response: CreatePushResponse = createResponse ?? {
            pushes: generatedPushes,
            skipped: [],
          }
          for (const push of response.pushes) {
            pushesById.set(push.id, push)
          }
          return HttpResponse.json(response, { status: 202 })
        })
    )
  )

export const postPolarisStatusLookup = ({
  handler,
  lookupResponse,
}: { handler?: Handler; lookupResponse?: PolarisStatusLookupResponse } = {}) =>
  http.post(
    '/api/v1/pushes/polaris-status',
    withAuth(
      handler ??
        (async ({ request }) => {
          const body = CreatePushRequestSchema.parse(await request.json().catch(() => ({})))
          const triageResultIds = body.triage_result_ids ?? []
          // Default to MATCH so a test that does not care about Polaris state is not gated on a dialog.
          const response: PolarisStatusLookupResponse = lookupResponse ?? {
            statuses: triageResultIds.map(triageResultId => ({
              triage_result_id: triageResultId,
              state: 'match',
            })),
            skipped: [],
          }
          return HttpResponse.json(response)
        })
    )
  )

export const getPushesByIds = ({
  handler,
  pushesByIdOverride,
}: {
  handler?: Handler
  pushesByIdOverride?: Partial<Record<string, PushResponse>>
} = {}) =>
  http.get(
    '/api/v1/pushes',
    withAuth(
      handler ??
        (({ request }) => {
          const url = new URL(request.url)
          const rawIds = url.searchParams.getAll('ids')
          const ids = rawIds.flatMap(raw => raw.split(',').filter(id => id.length > 0))
          const resolved = ids
            .map(id => pushesByIdOverride?.[id] ?? pushesById.get(id))
            .filter((push): push is PushResponse => push !== undefined)
          const response: GetPushesResponse = { pushes: resolved }
          return HttpResponse.json(response)
        })
    )
  )

export const deleteRepositoryIntegration = ({ handler }: { handler?: Handler } = {}) =>
  http.delete(
    '/api/v1/repositories/:repositoryId/integrations/:integrationId',
    withAuth(handler ?? (() => new HttpResponse(null, { status: 204 })))
  )

export const putRepositoryIntegration = ({ handler }: { handler?: Handler } = {}) =>
  http.put(
    '/api/v1/repositories/:repositoryId/integrations/:integrationId',
    withAuth(handler ?? (() => HttpResponse.json(FakeRepositoryIntegration(), { status: 200 })))
  )

export const putUpdateWorkflow = ({ handler }: { handler?: Handler } = {}) =>
  http.put(
    '/api/v1/workflows/:workflow_id',
    withAuth(
      handler ??
        (async () => {
          return HttpResponse.json(FakeWorkflow({ id: 'abcd', name: 'Updated workflow' }), { status: 200 })
        })
    )
  )

export const deleteWorkflow = ({ handler }: { handler?: Handler } = {}) =>
  http.delete('/api/v1/workflows/:workflow_id', withAuth(handler ?? (() => new HttpResponse(null, { status: 204 }))))

export const deleteRepository = ({ handler }: { handler?: Handler } = {}) =>
  http.delete(
    '/api/v1/repositories/:repository_id',
    withAuth(handler ?? (() => new HttpResponse(null, { status: 204 })))
  )

export const deleteScan = ({ handler }: { handler?: Handler } = {}) =>
  http.delete('/api/v1/scans/:scanId', withAuth(handler ?? (() => new HttpResponse(null, { status: 204 }))))

export const postAnalysisCancellation = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/scans/:scanId/analysis-cancellations',
    withAuth(
      handler ??
        (async ({ params }) => {
          const scanId = params.scanId
          if (!scanId) {
            return new HttpResponse(JSON.stringify({ message: 'Bad Request: scanId is required' }), { status: 400 })
          }
          return new HttpResponse(JSON.stringify(FakeAnalysisCancellation()), {
            status: 201,
            headers: { 'Content-Type': 'application/json' },
          })
        })
    )
  )

const defaultAnalysisStreamEvents: AnalysisStreamEvent[] = [
  { type: 'start', id: 'workflow-mock', timestamp: new Date().toISOString() },
  {
    type: 'data-progress-finding',
    content: {
      timestamp: new Date().toISOString(),
      finding_id: 'test-finding',
      type: 'triage',
      status: 'in_progress',
      title: 'Analyzing code',
      description: 'Starting analysis',
      completed_events: 1,
      total_events: 3,
    },
  },
  {
    type: 'data-progress-finding',
    content: {
      timestamp: new Date().toISOString(),
      finding_id: 'test-finding',
      type: 'triage',
      status: 'in_progress',
      title: 'Running analysis',
      description: 'Processing code patterns',
      completed_events: 2,
      total_events: 3,
    },
  },
  {
    type: 'data-progress-finding',
    content: {
      timestamp: new Date().toISOString(),
      finding_id: 'test-finding',
      type: 'triage',
      status: 'completed',
      title: 'Analysis complete',
      description: 'Test event',
      completed_events: 3,
      total_events: 3,
    },
  },
  {
    type: 'data-progress-finding-complete',
    content: { finding_id: 'test-finding', type: 'triage', resultType: 'TRIAGED', triageStatus: 'TRUE_POSITIVE' },
  },
  {
    type: 'workflow',
    content: { status: 'completed', title: 'Analysis complete', description: 'Finished analyzing findings' },
  },
  { type: 'finish', id: 'workflow-mock', timestamp: new Date().toISOString(), usage: {} },
]

export const getAnalysisStream = ({
  events = defaultAnalysisStreamEvents,
  handler,
}: { events?: AnalysisStreamEvent[]; handler?: () => Response } = {}) =>
  http.get('/api/v1/scans/:scan_id/events', () => {
    if (handler) {
      return handler()
    }

    // Named frames, as the platform sends them: it sets the `event:` name from the event's top-level
    // `type`, and `useGetResultActivitySSE` filters on that name. A bare `data:` line would bypass the
    // allowlist entirely and leave it untested end to end.
    const sseBody = events.map(event => `event: ${event.type}\ndata: ${JSON.stringify(event)}\n\n`).join('')

    return new HttpResponse(sseBody, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
    })
  })

export const getResultActivityStream = ({
  events = defaultAnalysisStreamEvents,
  handler,
}: { events?: AnalysisStreamEvent[]; handler?: () => Response } = {}) =>
  http.get('/api/v1/analysis-results/:result_id/activity', () => {
    if (handler) {
      return handler()
    }

    // Named frames, as the platform sends them: it sets the `event:` name from the event's top-level
    // `type`, and `useGetResultActivitySSE` filters on that name. A bare `data:` line would bypass the
    // allowlist entirely and leave it untested end to end.
    const sseBody = events.map(event => `event: ${event.type}\ndata: ${JSON.stringify(event)}\n\n`).join('')

    return new HttpResponse(sseBody, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
    })
  })

export const postTriageFeedbackSubmission = ({ handler, repositoryId }: PostFeedbackSubmissionOptions = {}) =>
  http.post(
    '/api/v1/triage-results/:resultId/feedback-submissions',
    withAuth(handler ?? defaultPostFeedbackHandler({ repositoryId }))
  )

export const postFixFeedbackSubmission = ({ handler, repositoryId }: PostFeedbackSubmissionOptions = {}) =>
  http.post(
    '/api/v1/fix-results/:resultId/feedback-submissions',
    withAuth(handler ?? defaultPostFeedbackHandler({ repositoryId }))
  )

export const postScaFeedbackSubmission = ({ handler, repositoryId }: PostFeedbackSubmissionOptions = {}) =>
  http.post(
    '/api/v1/sca-results/:resultId/feedback-submissions',
    withAuth(handler ?? defaultPostFeedbackHandler({ repositoryId }))
  )

const feedbackStorageKey = (resultId: string) => `msw:feedback:${resultId}`

const storeFeedback = (resultId: string, submission: Record<string, unknown>) => {
  try {
    localStorage.setItem(feedbackStorageKey(resultId), JSON.stringify(submission))
  } catch {
    // localStorage unavailable (e.g. Node/jsdom in tests) — silently skip
  }
}

const loadFeedback = (resultId: string): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem(feedbackStorageKey(resultId))
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null
  } catch {
    return null
  }
}

const feedbackSubmissionsResponse = (resultId: string, items: Record<string, unknown>[]) => ({
  _links: {
    self: { href: `/api/v1/results/${resultId}/feedback-submissions` },
    first: { href: `/api/v1/results/${resultId}/feedback-submissions` },
  },
  total: items.length,
  page: { number: 0, size: 10 },
  _embedded: { items },
})

const defaultGetFeedbackHandler =
  (): Handler =>
  ({ params }) => {
    const resultId = params.resultId as string
    const existing = loadFeedback(resultId)
    const items = existing ? [existing] : []
    return HttpResponse.json(feedbackSubmissionsResponse(resultId, items))
  }

type PostFeedbackSubmissionOptions = {
  handler?: Handler
  /**
   * The repository the result belongs to. The real API resolves it from the result → scan →
   * repository; the mock serves no result resource to follow, so a caller that wants the platform's
   * membership checks names the repository here.
   */
  repositoryId?: string
}

const feedbackProblem = ({
  type,
  title,
  status,
  detail = title,
  ...extensions
}: { type: string; title: string; status: number; detail?: string } & Record<string, unknown>) =>
  HttpResponse.json(
    { type, title, detail, status, ...extensions },
    { status, headers: { 'Content-Type': 'application/problem+json' } }
  )

/**
 * Mirrors the platform's `FeedbackService.resolveScope`: an omitted scope is the repository; at
 * project scope the repository's current project is read from the project store, an ungrouped
 * repository is refused (422), a displayed `project_id` that is no longer the current one is refused
 * (409, naming the current one), and an omitted `project_id` takes the current project. With no
 * `repositoryId` the membership is unknowable, so the project the client displayed is taken as the
 * current one — refusing every project-scoped submission would make the default handlers useless
 * for project scope.
 */
const defaultPostFeedbackHandler =
  ({ repositoryId }: Pick<PostFeedbackSubmissionOptions, 'repositoryId'> = {}): Handler =>
  async ({ params, request }) => {
    const resultId = params.resultId as string
    const existing = loadFeedback(resultId)
    if (existing) {
      return feedbackProblem({
        type: 'about:blank',
        title: 'Conflict',
        status: 409,
        detail: `Feedback already exists for result: ${resultId}`,
      })
    }
    const body = (await request.json()) as {
      rating: string
      text: string
      scope?: string
      project_id?: string
    }
    const scope = body.scope ?? 'repository'
    const resolveCurrentProjectId = () => {
      if (scope !== 'project') {
        return undefined
      }
      if (!repositoryId) {
        return body.project_id
      }
      return projectStore.membershipFor(repositoryId, { embedProject: true })?._embedded?.project?.id
    }
    const currentProjectId = resolveCurrentProjectId()
    if (scope === 'project' && !currentProjectId) {
      return feedbackProblem({
        type: 'pixee:problems/api/feedback-submissions/repository-ungrouped',
        title: 'Repository is not in a project',
        status: 422,
      })
    }
    if (currentProjectId && body.project_id !== undefined && body.project_id !== currentProjectId) {
      return feedbackProblem({
        type: 'pixee:problems/api/feedback-submissions/project-changed',
        title: 'Repository moved to another project',
        status: 409,
        project_id: currentProjectId,
      })
    }
    const submission = {
      id: createId(),
      rating: body.rating,
      text: body.text,
      scope,
      createdAt: new Date().toISOString(),
      username: 'test-user',
      _links: currentProjectId ? { project: { href: `/api/v1/projects/${currentProjectId}` } } : undefined,
    }
    storeFeedback(resultId, submission)
    return HttpResponse.json(submission, { status: 201 })
  }

export const getTriageFeedbackSubmissions = ({ handler }: { handler?: Handler } = {}) =>
  http.get('/api/v1/triage-results/:resultId/feedback-submissions', withAuth(handler ?? defaultGetFeedbackHandler()))

export const getFixFeedbackSubmissions = ({ handler }: { handler?: Handler } = {}) =>
  http.get('/api/v1/fix-results/:resultId/feedback-submissions', withAuth(handler ?? defaultGetFeedbackHandler()))

export const getScaFeedbackSubmissions = ({ handler }: { handler?: Handler } = {}) =>
  http.get('/api/v1/sca-results/:resultId/feedback-submissions', withAuth(handler ?? defaultGetFeedbackHandler()))

const DEFAULT_REPOSITORY: Repository = FakeRepository({
  // Fixed, so a Beacon run started under dev:mocks still belongs to this repository after a reload.
  id: '6d1f3c2a-8b4e-4f7a-9c5d-2e8b1a7f4c3d',
  type: 'github',
  name: 'owner/example-project',
  full_name: 'owner/example-project',
  owner: 'owner',
  html_url: 'https://github.com/owner/example-project',
})

// Shared between getScan and getAnalyses so scan.id and the embedded scan ID coordinate.
// The embedded integration carries an instance name so instance attribution renders in mocks;
// capabilities stays empty to leave the triage-push affordances disabled as before.
const DEFAULT_SCAN = FakeScan({
  _embedded: {
    integration: { id: 'default-integration', type: 'datadog', name: 'Instance 1' },
  },
})

/**
 * Stands in for the repository of a scan when the request did not filter by one, drawn once so the
 * unfiltered scans list is as stable between requests as the per-repository ones.
 */
const UNFILTERED_SCANS_REPOSITORY_ID = faker.string.uuid()

/** The generated scans per repository and requested detector. @see getDefaultScansForRepository */
const defaultScansByRepository = new Map<string, ScanResponse[]>()

/**
 * Returns a repository's twenty scans, generating them on the first request and reusing them after.
 *
 * `FakeScan()` draws a new uuid and picks a random detector on every call, so generating the list
 * per request made one URL answer differently each time. `FindingsTabWithProviders` reads the scan
 * it renders from `/api/v1/scans?page-size=1&repository-ids=…&tools=…`, and `useNewerScanBanner`
 * refetches that same query key every 5 s, so Repo details > Findings kept swapping to a different
 * scan id — or, when none of the twenty drew the selected tool, to no scan at all — and the header
 * above the table emptied and refilled every five seconds. Reusing the list also makes the tool
 * tabs agree with the per-tool scan lookup behind each of them.
 */
const getDefaultScansForRepository = (repositoryId: string, detector?: Tool): ScanResponse[] => {
  const cacheKey = `${repositoryId}:${detector ?? 'any'}`
  const generated = defaultScansByRepository.get(cacheKey)
  if (generated) {
    return generated
  }

  // Instance names alternate so the list shows attribution for more than one instance of a tool.
  const scans = Array.from({ length: 20 }, (_, index) => {
    const instanceNumber = (index % 2) + 1
    return FakeScan({
      detector,
      _links: { repository: { href: `/api/v1/repositories/${repositoryId}` } },
      _embedded: {
        integration: {
          id: `integration-${instanceNumber}`,
          type: 'datadog',
          name: `Instance ${instanceNumber}`,
        },
      },
    })
  })

  defaultScansByRepository.set(cacheKey, scans)
  return scans
}

// =============================================
// Coding Guidelines
// =============================================

const buildCodingGuidelineLinks = (orgId: string, id: string) => ({
  self: { href: `/api/v1/organizations/${orgId}/coding-guidelines/${id}` },
  content: { href: `/api/v1/organizations/${orgId}/coding-guidelines/${id}/content` },
  events: { href: `/api/v1/organizations/${orgId}/coding-guidelines/${id}/events` },
})

let codingGuidelinesStore: CodingGuidelinesResponse[] = [
  {
    id: 'mock-cg-1',
    display_name: 'OWASP Application Security Verification Standard L2',
    filename: 'asvs-l2-checklist.pdf',
    size: 2_203_648,
    content_type: 'application/pdf',
    status: 'ready',
    uploaded_by: 'alice.chen',
    uploaded_at: '2026-03-12T14:22:00Z',
    _links: buildCodingGuidelineLinks('default-organization', 'mock-cg-1'),
  },
  {
    id: 'mock-cg-2',
    display_name: 'Secure Code Review Checklist',
    filename: 'secure-review-checklist.docx',
    size: 126_976,
    content_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    status: 'ready',
    uploaded_by: 'bob.smith',
    uploaded_at: '2026-04-01T09:05:00Z',
    _links: buildCodingGuidelineLinks('default-organization', 'mock-cg-2'),
  },
  {
    id: 'mock-cg-3',
    display_name: 'API Security Best Practices',
    filename: 'api-security-best-practices.md',
    size: 18_432,
    content_type: 'text/markdown',
    status: 'ready',
    uploaded_by: 'carol.jones',
    uploaded_at: '2026-04-15T16:47:00Z',
    _links: buildCodingGuidelineLinks('default-organization', 'mock-cg-3'),
  },
  {
    id: 'mock-cg-4',
    display_name: 'Dependency Management Policy',
    filename: 'dep-management-policy.txt',
    size: 9_216,
    content_type: 'text/plain',
    status: 'ready',
    uploaded_by: 'dave.patel',
    uploaded_at: '2026-02-28T11:30:00Z',
    _links: buildCodingGuidelineLinks('default-organization', 'mock-cg-4'),
  },
  {
    id: 'mock-cg-5',
    display_name: 'Frontend Security Standards',
    filename: 'frontend-security-standards.html',
    size: 34_816,
    content_type: 'text/html',
    status: 'ready',
    uploaded_by: 'eve.martin',
    uploaded_at: '2026-03-28T13:15:00Z',
    _links: buildCodingGuidelineLinks('default-organization', 'mock-cg-5'),
  },
  {
    id: 'mock-cg-6',
    display_name: 'Java Secure Coding Guidelines',
    filename: 'java-secure-coding.pdf',
    size: 1_677_312,
    content_type: 'application/pdf',
    status: 'ready',
    uploaded_by: 'bob.smith',
    uploaded_at: '2026-05-01T10:00:00Z',
    _links: buildCodingGuidelineLinks('default-organization', 'mock-cg-6'),
  },
]

export const getCodingGuidelines = ({ handler }: { handler?: Handler } = {}) =>
  http.get(
    '/api/v1/organizations/:organizationId/coding-guidelines',
    withAuth(handler ?? (() => HttpResponse.json(codingGuidelinesStore)))
  )

export const postCodingGuideline = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/organizations/:organizationId/coding-guidelines',
    withAuth(
      handler ??
        (async ({ request, params }) => {
          const organizationId = String(params.organizationId)
          const formData = await request.formData()
          const displayNameEntry = formData.get('display_name')
          const fileEntry = formData.get('file')
          const file = fileEntry instanceof File ? fileEntry : undefined
          const id = createId()
          const newDoc: CodingGuidelinesResponse = {
            id,
            display_name: typeof displayNameEntry === 'string' ? displayNameEntry : (file?.name ?? 'Untitled'),
            filename: file?.name ?? 'unknown',
            size: file?.size ?? 0,
            content_type: file?.type !== undefined && file.type !== '' ? file.type : 'application/octet-stream',
            status: 'processing',
            uploaded_by: 'mock.user',
            uploaded_at: DateTime.now().toISO(),
            _links: buildCodingGuidelineLinks(organizationId, id),
          }
          codingGuidelinesStore = [newDoc, ...codingGuidelinesStore]
          return HttpResponse.json(newDoc, { status: 201 })
        })
    )
  )

export const patchCodingGuideline = ({ handler }: { handler?: Handler } = {}) =>
  http.patch(
    '/api/v1/organizations/:organizationId/coding-guidelines/:id',
    withAuth(
      handler ??
        (async ({ request, params }) => {
          const id = String(params.id)
          const body = (await request.json()) as { display_name?: string }
          const idx = codingGuidelinesStore.findIndex(d => d.id === id)
          if (idx === -1) {
            return new HttpResponse(null, { status: 404 })
          }
          const existing = codingGuidelinesStore[idx]
          invariant(existing, 'existing doc must be defined when idx is found')
          const updated: CodingGuidelinesResponse = {
            ...existing,
            display_name: body.display_name ?? existing.display_name,
            updated_at: DateTime.now().toISO(),
          }
          codingGuidelinesStore = codingGuidelinesStore.map((d, i) => (i === idx ? updated : d))
          return HttpResponse.json(updated)
        })
    )
  )

export const deleteCodingGuideline = ({ handler }: { handler?: Handler } = {}) =>
  http.delete(
    '/api/v1/organizations/:organizationId/coding-guidelines/:id',
    withAuth(
      handler ??
        (({ params }) => {
          const id = String(params.id)
          codingGuidelinesStore = codingGuidelinesStore.filter(d => d.id !== id)
          return new HttpResponse(null, { status: 204 })
        })
    )
  )

export const getCodingGuidelineEvents = () =>
  http.get(
    '/api/v1/organizations/:organizationId/coding-guidelines/:id/events',
    withAuth(({ params }) => {
      const id = String(params.id)
      const encoder = new TextEncoder()
      const phases: { phase: string; delayMs: number }[] = [
        { phase: 'received', delayMs: 1_000 },
        { phase: 'extracting', delayMs: 1_000 },
        { phase: 'indexing', delayMs: 1_500 },
        { phase: 'ready', delayMs: 1_000 },
      ]
      const stream = new ReadableStream({
        start: async controller => {
          for (const { phase, delayMs } of phases) {
            await new Promise(resolve => setTimeout(resolve, delayMs))
            if (phase === 'ready') {
              codingGuidelinesStore = codingGuidelinesStore.map(d => (d.id === id ? { ...d, status: 'ready' } : d))
            }
            controller.enqueue(
              encoder.encode(
                `event: ${phase}\ndata: ${JSON.stringify({ phase, created_at: new Date().toISOString() })}\n\n`
              )
            )
          }
          controller.close()
        },
      })
      return new HttpResponse(stream, {
        headers: {
          'Content-Type': 'text/event-stream',
          'Cache-Control': 'no-cache',
          Connection: 'keep-alive',
        },
      })
    })
  )

export const postSupportBundle = ({ handler }: { handler?: Handler } = {}) =>
  http.post(
    '/api/v1/support-bundles',
    withAuth(
      handler ??
        (() => {
          const id = faker.string.uuid()
          return HttpResponse.json(
            {
              id,
              phase: 'pending',
              _links: {
                self: { href: `/api/v1/support-bundles/${id}` },
                events: { href: `/api/v1/support-bundles/${id}` },
              },
            },
            { status: 202 }
          )
        })
    )
  )

/**
 * Default API root carries the support-bundles link so capability-gated UI is rendered in tests that
 * don't override. Pass `{ hasSupportBundles: false }` to drop it.
 */
export const getApiRoot = ({
  hasSupportBundles = true,
  handler,
}: { hasSupportBundles?: boolean; handler?: Handler } = {}) =>
  http.get(
    '/api/v1',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json({
            _links: {
              self: { href: '/api/v1' },
              ...(hasSupportBundles ? { 'support-bundles': { href: '/api/v1/support-bundles' } } : {}),
            },
          }))
    )
  )

/**
 * SSE phase stream for a single support bundle. Default emits a pending → running → succeeded
 * sequence; tests override `events` (or pass a custom `handler`) to drive specific phase paths.
 */
export const getSupportBundleStream = ({
  events = [
    { id: 'mock-bundle-id', phase: 'pending' as const, elapsed_seconds: 0 },
    { id: 'mock-bundle-id', phase: 'running' as const, elapsed_seconds: 5 },
    { id: 'mock-bundle-id', phase: 'succeeded' as const, elapsed_seconds: 42 },
  ],
  handler,
}: {
  events?: { id: string; phase: 'pending' | 'running' | 'succeeded' | 'failed'; elapsed_seconds: number }[]
  handler?: () => Response
} = {}) =>
  http.get('/api/v1/support-bundles/:id', () => {
    if (handler) return handler()
    const sseBody = events.map(event => `event: phase\ndata: ${JSON.stringify(event)}\n\n`).join('')
    return new HttpResponse(sseBody, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
    })
  })

// =============================================
// Threat model (ENH-539)
// =============================================

/**
 * Encodes phase events as an SSE body, or 404s when a stream has nothing to serve. Every
 * generation-feature stream is shaped the same way (`event: <phase|status>` + a JSON `data:` line),
 * so they all share this rather than each carrying a copy of the encoding.
 */
const toPhaseEventSseResponse = (events?: readonly { status: string; phase: string }[]) =>
  events
    ? new HttpResponse(
        events
          .map(event => {
            const eventName = event.status === 'processing' ? event.phase : event.status
            return `event: ${eventName}\ndata: ${JSON.stringify(event)}\n\n`
          })
          .join(''),
        { headers: { 'Content-Type': 'text/event-stream' } }
      )
    : new HttpResponse(null, { status: 404 })

export const getRepositoryThreatModel = ({
  handler,
  threatModel,
}: { handler?: Handler; threatModel?: ThreatModelResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/threat-model',
    withAuth(
      handler ?? (() => (threatModel ? HttpResponse.json(threatModel) : new HttpResponse(null, { status: 404 })))
    )
  )

export const postRepositoryThreatModel = ({
  handler,
  threatModel,
}: { handler?: Handler; threatModel?: ThreatModelResponse } = {}) =>
  http.post(
    '/api/v1/repositories/:repositoryId/threat-model',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            threatModel ??
              FakeThreatModel({
                status: 'processing',
                phase: 'queued',
                generatedAt: undefined,
                _links: { self: { href: '/api/v1/repositories/fake-repository-id/threat-model' } },
              }),
            { status: 202 }
          ))
    )
  )

// =============================================
// Opengrep rules
// =============================================

export const getRepositoryOpenGrepRules = ({
  handler,
  openGrepRules,
}: { handler?: Handler; openGrepRules?: OpenGrepRulesResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/opengrep-rules',
    withAuth(
      handler ?? (() => (openGrepRules ? HttpResponse.json(openGrepRules) : new HttpResponse(null, { status: 404 })))
    )
  )

export const postRepositoryOpenGrepRules = ({
  handler,
  openGrepRules,
}: { handler?: Handler; openGrepRules?: OpenGrepRulesResponse } = {}) =>
  http.post(
    '/api/v1/repositories/:repositoryId/opengrep-rules',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            openGrepRules ??
              FakeOpenGrepRules({
                status: 'processing',
                phase: 'queued',
                generatedAt: undefined,
                _links: { self: { href: '/api/v1/repositories/fake-repository-id/opengrep-rules' } },
              }),
            { status: 202 }
          ))
    )
  )

export const getRepositoryOpenGrepRulesContent = ({
  handler,
  content,
}: { handler?: Handler; content?: OpenGrepRuleSet } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/opengrep-rules/content',
    withAuth(handler ?? (() => HttpResponse.json(content ?? FakeOpenGrepRuleSet())))
  )

export const getRepositoryOpenGrepRulesEvents = ({
  handler,
  events,
}: { handler?: Handler; events?: OpenGrepRulesStreamEvent[] } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/opengrep-rules/events',
    withAuth(handler ?? (() => toPhaseEventSseResponse(events)))
  )

export const getRepositoryThreatModelContent = ({
  handler,
  content,
}: { handler?: Handler; content?: ThreatModelContent } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/threat-model/content',
    withAuth(handler ?? (() => HttpResponse.json(content ?? exampleThreatModelContent)))
  )

export const getRepositoryThreatModelEvents = ({
  handler,
  events,
}: { handler?: Handler; events?: ThreatModelStreamEvent[] } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/threat-model/events',
    withAuth(handler ?? (() => toPhaseEventSseResponse(events)))
  )

/**
 * Fabricated org-level risk rollup. Nothing serves this outside mock mode — see `repository-risk.ts`.
 *
 * The default seed covers every state the view has to render: one repository per score band, one still
 * processing, and one whose generation failed. Repositories with no threat model at all are simply
 * absent, which is what the real API's 404 amounts to.
 */
export const getRepositoryRisk = ({ handler, risks }: { handler?: Handler; risks?: RepositoryRisk[] } = {}) =>
  http.get(
    REPOSITORY_RISK_PATH,
    withAuth(handler ?? (() => (risks ? HttpResponse.json({ items: risks }) : new HttpResponse(null, { status: 404 }))))
  )

export const exampleRepositoryRisks: RepositoryRisk[] = [
  FakeRepositoryRisk({
    fullName: 'acme/storefront-api',
    riskScore: 91,
    truePositiveCount: 4,
    rationale: 'Public payment surface with unverified webhook signatures',
    details:
      'The storefront API terminates public traffic and accepts the payment provider’s settlement ' +
      'webhook without verifying its HMAC signature, so an attacker who learns the path can mark ' +
      'arbitrary orders as paid. The same service reads the orders datastore directly, which means the ' +
      'gap is one hop from customer records rather than two.',
    factors: [
      { dimension: 'attack-surface', detail: 'Internet-facing, accepts unauthenticated webhook callbacks' },
      { dimension: 'authentication', detail: 'Settlement webhook HMAC signature is never verified' },
      { dimension: 'blast-radius', detail: 'Reads and writes order and customer contact records directly' },
    ],
  }),
  FakeRepositoryRisk({
    fullName: 'acme/admin-console',
    riskScore: 68,
    truePositiveCount: 2,
    rationale: 'Internal console bypasses the service authorization boundary',
    details:
      'The admin console reads and writes the orders database directly for support overrides, so ' +
      'service-level authorization and audit logging are skipped entirely. Exposure is limited to ' +
      'authenticated staff, which keeps this below the storefront, but the blast radius is the whole ' +
      'orders datastore.',
    factors: [
      { dimension: 'attack-surface', detail: 'Authenticated staff only, on an IP allowlist' },
      { dimension: 'authorization', detail: 'Writes to the datastore without passing through the order service' },
      { dimension: 'observability', detail: 'Support overrides bypass service-level audit logging' },
    ],
  }),
  FakeRepositoryRisk({
    fullName: 'acme/search-index',
    riskScore: 47,
    truePositiveCount: 1,
    rationale: 'Indexes product data but shares the datastore trust zone',
    details:
      'The search indexer sits inside the restricted data tier and reads product records only. Its ' +
      'placement inside that zone means a compromise reaches the datastore, but the data it handles is ' +
      'not sensitive on its own.',
    factors: [
      { dimension: 'blast-radius', detail: 'Shares the datastore trust zone with the order service' },
      { dimension: 'attack-surface', detail: 'Runs inside the restricted data tier, no public listener' },
    ],
  }),
  FakeRepositoryRisk({
    fullName: 'acme/notification-service',
    riskScore: 24,
    truePositiveCount: 0,
    rationale: 'Outbound only, no customer data at rest',
    details:
      'The notification service sends email and push messages through third-party providers and keeps ' +
      'nothing at rest. Its threat model surfaces provider credential handling as the only meaningful ' +
      'concern, and the secrets manager already covers it.',
    factors: [
      { dimension: 'blast-radius', detail: 'None — messages are relayed, never stored' },
      { dimension: 'transport', detail: 'Outbound TLS enforced, no inbound listener' },
    ],
  }),
  FakeRepositoryRisk({
    fullName: 'acme/design-tokens',
    riskScore: 4,
    truePositiveCount: 0,
    rationale: 'Build-time package with no runtime surface',
    details:
      'A published package of design tokens with no network surface, no datastore and no runtime. Its ' +
      'threat model identifies supply-chain publication as the only path worth watching.',
    factors: [
      { dimension: 'attack-surface', detail: 'None — consumed at build time, no runtime' },
      { dimension: 'dependencies', detail: 'Publication is the only path worth watching' },
    ],
  }),
  FakeRepositoryRisk({
    fullName: 'acme/billing-worker',
    threatModelStatus: 'processing',
    generatedAt: undefined,
    commitSha: undefined,
    riskScore: 0,
    truePositiveCount: 0,
    rationale: 'Threat model still generating',
    details: 'A score appears once the model finishes generating.',
    factors: [],
    scanId: undefined,
  }),
  FakeRepositoryRisk({
    fullName: 'acme/legacy-importer',
    threatModelStatus: 'failed',
    generatedAt: undefined,
    commitSha: undefined,
    riskScore: 0,
    truePositiveCount: 0,
    rationale: 'Threat model generation failed',
    details: 'The last generation attempt failed, so there is no model to score.',
    factors: [],
    scanId: undefined,
  }),
]

export const getRepositoryThreatModelRefreshRequests = ({
  handler,
  list,
}: { handler?: Handler; list?: RefreshRequestsResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/threat-model/refresh-requests',
    withAuth(handler ?? (() => HttpResponse.json(list ?? FakeRefreshRequestList({ items: [] }))))
  )

export const getRepositoryThreatModelRefreshRequest = ({
  handler,
  request,
}: { handler?: Handler; request?: RefreshRequestResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/threat-model/refresh-requests/:requestId',
    withAuth(handler ?? (() => (request ? HttpResponse.json(request) : new HttpResponse(null, { status: 404 }))))
  )

// Demo state machine for mock mode: POST starts a run that advances one phase every
// couple of seconds and then completes with the example document (served as JSON by the content
// handler). The events endpoint stays 404 so the UI exercises its GET-polling fallback.
const THREAT_MODEL_DEMO_PHASE_MS = 2_000
const threatModelDemoState: { startedAtMs?: number; completedModel?: ThreatModelResponse } = {
  completedModel: FakeThreatModel({ generatedAt: '2026-08-07T14:20:00Z' }),
}

const threatModelDemoGetHandler: Handler = () => {
  if (threatModelDemoState.startedAtMs !== undefined) {
    const phases = ThreatModelPhaseSchema.options
    const phaseIndex = Math.floor((Date.now() - threatModelDemoState.startedAtMs) / THREAT_MODEL_DEMO_PHASE_MS)
    if (phaseIndex < phases.length) {
      return HttpResponse.json(
        FakeThreatModel({
          status: 'processing',
          phase: phases[phaseIndex],
          generatedAt: undefined,
          _links: threatModelDemoState.completedModel?._links ?? {
            self: { href: '/api/v1/repositories/fake-repository-id/threat-model' },
          },
        })
      )
    }

    threatModelDemoState.completedModel = FakeThreatModel({ generatedAt: DateTime.now().toISO() })
    threatModelDemoState.startedAtMs = undefined
  }

  if (threatModelDemoState.completedModel) {
    return HttpResponse.json(threatModelDemoState.completedModel)
  }

  return new HttpResponse(null, { status: 404 })
}

const threatModelDemoPostHandler: Handler = () => {
  if (threatModelDemoState.startedAtMs === undefined) {
    threatModelDemoState.startedAtMs = Date.now()
  }
  return HttpResponse.json(
    FakeThreatModel({
      status: 'processing',
      phase: 'queued',
      generatedAt: undefined,
      _links: threatModelDemoState.completedModel?._links ?? {
        self: { href: '/api/v1/repositories/fake-repository-id/threat-model' },
      },
    }),
    { status: 202 }
  )
}

// =============================================
// Exploit chains (ENH-555)
// =============================================

export const getRepositoryExploitChains = ({
  handler,
  exploitChains,
}: { handler?: Handler; exploitChains?: ExploitChainsResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/exploit-chains',
    withAuth(
      handler ?? (() => (exploitChains ? HttpResponse.json(exploitChains) : new HttpResponse(null, { status: 404 })))
    )
  )

export const postRepositoryExploitChains = ({
  handler,
  exploitChains,
}: { handler?: Handler; exploitChains?: ExploitChainsResponse } = {}) =>
  http.post(
    '/api/v1/repositories/:repositoryId/exploit-chains',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            exploitChains ??
              FakeExploitChains({
                status: 'processing',
                phase: 'queued',
                generatedAt: undefined,
                chains: [],
                _links: { self: { href: '/api/v1/repositories/fake-repository-id/exploit-chains' } },
              }),
            { status: 202 }
          ))
    )
  )

export const getRepositoryExploitChainsEvents = ({
  handler,
  events,
}: { handler?: Handler; events?: ExploitChainsStreamEvent[] } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/exploit-chains/events',
    withAuth(handler ?? (() => toPhaseEventSseResponse(events)))
  )

// Demo state machine for mock mode: POST starts a run that advances one phase every couple
// of seconds and then completes with example chains. The events endpoint stays 404 so the
// UI exercises its GET-polling fallback.
const EXPLOIT_CHAINS_DEMO_PHASE_MS = 2_000
const exploitChainsDemoState: { startedAtMs?: number; completedModel?: ExploitChainsResponse } = {}

const exploitChainsDemoGetHandler: Handler = () => {
  if (exploitChainsDemoState.startedAtMs !== undefined) {
    const phases = ExploitChainsPhaseSchema.options
    const phaseIndex = Math.floor((Date.now() - exploitChainsDemoState.startedAtMs) / EXPLOIT_CHAINS_DEMO_PHASE_MS)
    if (phaseIndex < phases.length) {
      return HttpResponse.json(
        FakeExploitChains({
          status: 'processing',
          phase: phases[phaseIndex],
          generatedAt: undefined,
          chains: [],
          _links: exploitChainsDemoState.completedModel?._links ?? {
            self: { href: '/api/v1/repositories/fake-repository-id/exploit-chains' },
          },
        })
      )
    }

    exploitChainsDemoState.completedModel = FakeExploitChains({ generatedAt: DateTime.now().toISO() })
    exploitChainsDemoState.startedAtMs = undefined
  }

  if (exploitChainsDemoState.completedModel) {
    return HttpResponse.json(exploitChainsDemoState.completedModel)
  }

  return new HttpResponse(null, { status: 404 })
}

const exploitChainsDemoPostHandler: Handler = () => {
  if (exploitChainsDemoState.startedAtMs === undefined) {
    exploitChainsDemoState.startedAtMs = Date.now()
  }
  return HttpResponse.json(
    FakeExploitChains({
      status: 'processing',
      phase: 'queued',
      generatedAt: undefined,
      chains: [],
      _links: exploitChainsDemoState.completedModel?._links ?? {
        self: { href: '/api/v1/repositories/fake-repository-id/exploit-chains' },
      },
    }),
    { status: 202 }
  )
}

// =============================================
// Finding overlays (ENH-…)
// =============================================

export const getRepositoryFindingOverlays = ({
  handler,
  findingOverlays,
}: { handler?: Handler; findingOverlays?: FindingOverlaysResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/finding-overlays',
    withAuth(
      handler ??
        (() => (findingOverlays ? HttpResponse.json(findingOverlays) : new HttpResponse(null, { status: 404 })))
    )
  )

export const postRepositoryFindingOverlays = ({
  handler,
  findingOverlays,
}: { handler?: Handler; findingOverlays?: FindingOverlaysResponse } = {}) =>
  http.post(
    '/api/v1/repositories/:repositoryId/finding-overlays',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            findingOverlays ??
              FakeFindingOverlays({
                status: 'processing',
                phase: 'queued',
                generatedAt: undefined,
                summary: undefined,
                _links: { self: { href: '/api/v1/repositories/fake-repository-id/finding-overlays' } },
              }),
            { status: 202 }
          ))
    )
  )

export const getRepositoryFindingOverlaysContent = ({
  handler,
  content,
}: { handler?: Handler; content?: FindingOverlayResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/finding-overlays/content',
    withAuth(handler ?? (() => (content ? HttpResponse.json(content) : HttpResponse.json(FakeFindingOverlayContent()))))
  )

export const getRepositoryFindingOverlaysEvents = ({
  handler,
  events,
}: { handler?: Handler; events?: FindingOverlaysStreamEvent[] } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/finding-overlays/events',
    withAuth(handler ?? (() => toPhaseEventSseResponse(events)))
  )

// =============================================
// Scorecards
// =============================================

export const getRepositoryScorecards = ({
  handler,
  scorecards,
}: { handler?: Handler; scorecards?: ScorecardsResponse } = {}) =>
  http.get(
    '/api/v1/repositories/:repositoryId/scorecards',
    withAuth(handler ?? (() => (scorecards ? HttpResponse.json(scorecards) : new HttpResponse(null, { status: 404 }))))
  )

export const postRepositoryScorecards = ({
  handler,
  scorecards,
}: { handler?: Handler; scorecards?: ScorecardsResponse } = {}) =>
  http.post(
    '/api/v1/repositories/:repositoryId/scorecards',
    withAuth(
      handler ??
        (() =>
          HttpResponse.json(
            scorecards ??
              FakeScorecards({
                status: 'processing',
                phase: 'queued',
                generatedAt: undefined,
                scorecard: undefined,
                _links: { self: { href: '/api/v1/repositories/fake-repository-id/scorecards' } },
              }),
            { status: 202 }
          ))
    )
  )

// Demo state machine for mock mode: POST starts a run that advances one phase every couple of
// seconds and then completes with an example scorecard payload. Scorecards has no SSE stream and
// no content sub-resource — polling on GET is the only observation channel. The initial completed
// model is seeded from the analysis-service example so opening the tab shows a realistic state
// without walking through generation first; POST triggers a regeneration on top of it.
const SCORECARDS_DEMO_PHASE_MS = 2_000
const scorecardsDemoState: { startedAtMs?: number; completedModel?: ScorecardsResponse } = {
  completedModel: EXAMPLE_SCORECARDS_RESPONSE,
}

const scorecardsDemoGetHandler: Handler = () => {
  if (scorecardsDemoState.startedAtMs !== undefined) {
    const phases = ScorecardsPhaseSchema.options
    const phaseIndex = Math.floor((Date.now() - scorecardsDemoState.startedAtMs) / SCORECARDS_DEMO_PHASE_MS)
    if (phaseIndex < phases.length) {
      return HttpResponse.json(
        FakeScorecards({
          status: 'processing',
          phase: phases[phaseIndex],
          generatedAt: undefined,
          scorecard: undefined,
          _links: scorecardsDemoState.completedModel?._links ?? {
            self: { href: '/api/v1/repositories/fake-repository-id/scorecards' },
          },
        })
      )
    }

    scorecardsDemoState.completedModel = FakeScorecards({ generatedAt: DateTime.now().toISO() })
    scorecardsDemoState.startedAtMs = undefined
  }

  if (scorecardsDemoState.completedModel) {
    return HttpResponse.json(scorecardsDemoState.completedModel)
  }

  return new HttpResponse(null, { status: 404 })
}

const scorecardsDemoPostHandler: Handler = () => {
  if (scorecardsDemoState.startedAtMs === undefined) {
    scorecardsDemoState.startedAtMs = Date.now()
  }
  return HttpResponse.json(
    FakeScorecards({
      status: 'processing',
      phase: 'queued',
      generatedAt: undefined,
      scorecard: undefined,
      _links: scorecardsDemoState.completedModel?._links ?? {
        self: { href: '/api/v1/repositories/fake-repository-id/scorecards' },
      },
    }),
    { status: 202 }
  )
}

const DEFAULT_REPOSITORIES: Repository[] = [
  DEFAULT_REPOSITORY,
  // The risk-bearing repositories share their ids with `exampleRepositoryRisks`, so the two mocks
  // actually join. Without them every row reads "No threat model": the repositories list and the
  // risk rollup are separate endpoints, and nothing correlates independently generated ids.
  ...exampleRepositoryRisks.map(risk =>
    FakeRepository({ id: risk.repositoryId, type: 'github', name: risk.fullName, full_name: risk.fullName })
  ),
  FakeRepository({ type: 'gitlab' }),
  FakeRepository({ type: 'azure' }),
  // Large enough to fill the two cap-state projects the prototype seeds (100 + 98) and still leave an
  // ungrouped tail to add from, so the cap is reachable by clicking rather than only by a unit test.
  ...Array(253)
    .fill(null)
    .map(() => FakeRepository()),
]

// The projects store reads the same fixtures the repositories list serves, so membership shows up on
// both sides and a move on one is visible on the other.
seedProjectStore({ repositories: DEFAULT_REPOSITORIES, projects: PROTOTYPE_PROJECT_SEEDS })

// The first repository carries a finished Beacon run; the rest have none until one is started.
seedBeaconRuns([DEFAULT_REPOSITORY.id])

export const defaultHandlers = {
  // First, so they answer for Beacon scans before the generic scan handlers below; for any other scan
  // they fall through to those.
  ...beaconHandlers(),
  ...freeformHandlers(),
  login: login(),
  logout: logout(),
  getTriageFeedbackSubmissions: getTriageFeedbackSubmissions(),
  getFixFeedbackSubmissions: getFixFeedbackSubmissions(),
  getScaFeedbackSubmissions: getScaFeedbackSubmissions(),
  postTriageFeedbackSubmission: postTriageFeedbackSubmission(),
  postFixFeedbackSubmission: postFixFeedbackSubmission(),
  postScaFeedbackSubmission: postScaFeedbackSubmission(),
  getSystemStatuses: getSystemStatuses([
    {
      _links: { self: { href: '/api/v1/system-statuses/analysis-service-model-reasoning' } },
      type: 'ai-model',
      id: 'analysis-service-model-reasoning',
      name: 'Analysis Service - Reasoning Model',
      status: 'up',
      description: 'AI model o3-mini is available and operational',
      model: 'o3-mini',
    },
    {
      _links: { self: { href: '/api/v1/system-statuses/analysis-service-model-fast' } },
      type: 'ai-model',
      id: 'analysis-service-model-fast',
      name: 'Analysis Service - Fast Model',
      status: 'up',
      description: 'AI model gpt-4o is available and operational',
      model: 'gpt-4o',
    },
  ]),
  getUser: getUser(FakeUser()),
  getCurrentUser: getCurrentUser(FakeCurrentUser()),
  getOrganizationPreferences: getOrganizationPreferences(),
  putOrganizationPreferences: putOrganizationPreferences(),
  getIntegrations: getIntegrations(),
  getIntegration: getIntegration(),
  getIntegrationTypeCounts: getIntegrationTypeCounts({ items: [] }),
  optionsIntegrations: optionsIntegrations(),
  postIntegration: postIntegration(),
  putIntegration: putIntegration(),
  deleteIntegration: deleteIntegration(),
  getRepository: getRepository({ fallback: DEFAULT_REPOSITORY }),
  getRepositories: getRepositories({ repositories: DEFAULT_REPOSITORIES }),
  ...projectHandlers(),
  // To toggle between SAST and SCA mocks, change the detector below and swap DEFAULT_FINDINGS in getScanFindings.
  // SAST: use FakeScan() (random SAST detector) with DEFAULT_FINDINGS = SAST_FINDINGS
  // SCA: use FakeScan({ detector: 'gitlab_dependency_scanning' }) with DEFAULT_FINDINGS = SCA_FINDINGS
  getScan: getScan({ scan: DEFAULT_SCAN }),
  getScans: getScans({
    handler: async ({ request }) => {
      const url = new URL(request.url)
      const pageNumber = Number(url.searchParams.get('page-number')) || 0
      const pageSize = Number(url.searchParams.get('page-size')) || 10
      const tools = url.searchParams.getAll('tools')
      const repositoryIds = url.searchParams.getAll('repository-ids')
      const repositoryId = repositoryIds[0] ?? UNFILTERED_SCANS_REPOSITORY_ID
      const analysisState = url.searchParams.get('analysis-state')
      // Stamped so a branch-filtered list (the repository page's branch selector) shows that branch.
      const branch = url.searchParams.get('branch') ?? undefined

      // When the caller filters to a single tool (e.g. the AI detection tab asks for `pixee_ai`),
      // stamp that detector so the seeded scans survive the filter below instead of coming back empty.
      const requestedDetector = tools.length === 1 ? ToolSchema.options.find(tool => tool === tools[0]) : undefined
      // Copies, so stamping a branch never writes into the cached seed list.
      let scans = getDefaultScansForRepository(repositoryId, requestedDetector).map(scan =>
        branch ? { ...scan, branch } : scan
      )
      if (tools.length > 0) {
        scans = scans.filter(s => tools.includes(s.detector))
      }
      if (analysisState === 'completed') {
        scans = scans.slice(0, Math.ceil(scans.length / 2))
      } else if (analysisState === 'in-progress') {
        scans = []
      } else if (analysisState === 'not-analyzed') {
        scans = []
      }

      const start = pageNumber * pageSize
      return HttpResponse.json(
        FakePaginatedScans({
          total: scans.length,
          page: { number: pageNumber, size: pageSize },
          _embedded: { items: scans.slice(start, start + pageSize) },
          distinct_branches: scans.length > 0 ? ['main'] : [],
        })
      )
    },
  }),
  getScanAnalysis: getScanAnalysis({
    scanAnalysis: FakeScanAnalysis({ current_state: { state: 'completed', timestamp: DateTime.now().toISO() } }),
  }),
  // Ages measured from the real clock, so `dev:mocks` shows recent imports; stories and tests that
  // assert on ages pass their own fixed `now`.
  getRepositoryBranches: getRepositoryBranches({ items: FakeRepositoryBranchList({ now: DateTime.now() }) }),
  getFindingArticle: getFindingArticle(),
  getTriageResultArticle: getTriageResultArticle(),
  getAnalysisPreferences: getAnalysisPreferences(),
  getFindingReport: getFindingReport(),
  getTriageResultReport: getTriageResultReport(),
  getTriageResultVerification: getTriageResultVerification(),
  getRepositoryThreatModel: getRepositoryThreatModel({ handler: threatModelDemoGetHandler }),
  postRepositoryThreatModel: postRepositoryThreatModel({ handler: threatModelDemoPostHandler }),
  // ENH-559: dev mocks serve the full WebGoat document so the JSON rendering has realistic content.
  getRepositoryThreatModelContent: getRepositoryThreatModelContent({ content: exampleThreatModelContent }),
  getRepositoryRisk: getRepositoryRisk({ risks: exampleRepositoryRisks }),
  getRepositoryThreatModelEvents: getRepositoryThreatModelEvents(),
  getRepositoryThreatModelRefreshRequests: getRepositoryThreatModelRefreshRequests({
    list: FakeRefreshRequestList({
      items: [
        FakeRefreshRequest({
          id: 'req-mock-1',
          state: 'regenerating',
          trigger_source: 'merged-pr',
          pull_request_number: 214,
          after_sha: '1a2b3c4d5e6f7890',
          triggered_at: '2026-08-04T14:12:00Z',
          _links: {
            self: { href: '/api/v1/repositories/fake-repository-id/threat-model/refresh-requests/req-mock-1' },
            repository: { href: '/api/v1/repositories/fake-repository-id' },
            'threat-model': { href: '/api/v1/repositories/fake-repository-id/threat-model' },
            commit: { href: 'https://github.com/acme/storefront/commit/1a2b3c4d5e6f7890' },
            'pull-request': { href: 'https://github.com/acme/storefront/pull/214' },
          },
        }),
        FakeRefreshRequest({
          id: 'req-mock-2',
          state: 'no-revision',
          trigger_source: 'direct-push',
          after_sha: '9f8e7d6c5b4a3210',
          triggered_at: '2026-08-04T09:47:00Z',
          decision_rationale:
            'Changes were limited to README updates and CI configuration; no production code or trust boundaries were touched.',
        }),
        FakeRefreshRequest({
          id: 'req-mock-3',
          state: 'failed',
          trigger_source: 'direct-push',
          after_sha: 'cafecafecafecafe',
          triggered_at: '2026-08-03T18:03:00Z',
          decision_rationale: 'Analysis-service returned a 502 while calling the decide endpoint.',
        }),
        FakeRefreshRequest({
          id: 'req-mock-4',
          state: 'completed',
          trigger_source: 'merged-pr',
          pull_request_number: 211,
          after_sha: 'deadbeefdeadbeef',
          triggered_at: '2026-08-02T11:22:00Z',
          _links: {
            self: { href: '/api/v1/repositories/fake-repository-id/threat-model/refresh-requests/req-mock-4' },
            repository: { href: '/api/v1/repositories/fake-repository-id' },
            'threat-model': { href: '/api/v1/repositories/fake-repository-id/threat-model' },
            commit: { href: 'https://github.com/acme/storefront/commit/deadbeefdeadbeef' },
            'pull-request': { href: 'https://github.com/acme/storefront/pull/211' },
          },
        }),
        FakeRefreshRequest({
          id: 'req-mock-5',
          state: 'completed',
          trigger_source: 'onboarding',
          after_sha: '111222333444555a',
          triggered_at: '2026-07-30T09:00:00Z',
          ref: undefined,
        }),
      ],
    }),
  }),
  getRepositoryThreatModelRefreshRequest: getRepositoryThreatModelRefreshRequest({
    request: FakeRefreshRequest({
      id: 'req-mock-1',
      state: 'regenerating',
      trigger_source: 'merged-pr',
      pull_request_number: 214,
      after_sha: '1a2b3c4d5e6f7890',
      threat_model_document_status: 'processing',
    }),
  }),
  getRepositoryExploitChains: getRepositoryExploitChains({ handler: exploitChainsDemoGetHandler }),
  postRepositoryExploitChains: postRepositoryExploitChains({ handler: exploitChainsDemoPostHandler }),
  getRepositoryExploitChainsEvents: getRepositoryExploitChainsEvents(),
  getRepositoryFindingOverlays: getRepositoryFindingOverlays({
    findingOverlays: FakeFindingOverlays({
      generatedAt: '2026-08-14T15:00:00Z',
      requestedBy: 'demo@pixee.ai',
      summary: {
        priorityCount: EXAMPLE_SCAN_PRIORITIZATION_CONTENT.priorities.length,
        clusterCount: EXAMPLE_SCAN_PRIORITIZATION_CONTENT.clusters.length,
        highestTier: 'critical',
      },
    }),
  }),
  postRepositoryFindingOverlays: postRepositoryFindingOverlays(),
  getRepositoryFindingOverlaysContent: getRepositoryFindingOverlaysContent({
    content: EXAMPLE_SCAN_PRIORITIZATION_CONTENT,
  }),
  getRepositoryFindingOverlaysEvents: getRepositoryFindingOverlaysEvents(),
  getRepositoryScorecards: getRepositoryScorecards({ handler: scorecardsDemoGetHandler }),
  postRepositoryScorecards: postRepositoryScorecards({ handler: scorecardsDemoPostHandler }),
  getScanFindings: getScanFindings(),
  getChangesetsForFix: getChangesetsForFix(),
  getFixResultChangesets: getFixResultChangesets(),
  getAzureOrganizations: getAzureOrganizations(),
  getAzureProjects: getAzureProjects(),
  getAzureRepositories: getAzureRepositories(),
  getBitbucketWorkspaces: getBitbucketWorkspaces(),
  getBitbucketRepositories: getBitbucketRepositories(),
  getAnalyses: getAnalyses({
    items: [
      FakeScanAnalysis({
        current_state: { state: 'completed', timestamp: '2024-01-15T10:39:15.000Z' },
        log: [
          { state: 'queued', timestamp: '2024-01-15T10:32:00.000Z' },
          { state: 'in-progress', timestamp: '2024-01-15T10:34:00.000Z' },
          { state: 'completed', timestamp: '2024-01-15T10:39:15.000Z' },
        ],
        _embedded: { scan: DEFAULT_SCAN },
      }),
      ...Array.from({ length: 999 }).map(() => FakeScanAnalysis()),
    ],
  }),
  getWorkflows: getWorkflows(),
  getScale: getScale(),
  getScales: getScales,
  getCodeqlScale: getCodeqlScale,
  getScanFinding: getScanFinding(),
  getScanFindingResults: getScanFindingResults(),
  getScaResultReport: getScaResultReport(),
  getScaResultArticle: getScaResultArticle(),
  getGitLabProjects: getGitLabProjects({
    items: Array(100)
      .fill(null)
      .map((_, index) =>
        FakeGitlabProject({
          name_with_namespace: `${index + 1} ${faker.company.name()}`,
        })
      ),
  }),
  getSonarProjects: getSonarProjects({
    items: ['juice-shop', 'WebGoat.NET', 'pygoat', 'NodeGoat'].map(name =>
      FakeSonarProject({ key: `pixee_${name}`, name })
    ),
  }),
  getRepositoryIntegrations: getRepositoryIntegrations(),
  postUploadScan: postUploadScan(),
  postUploadSbom: postUploadSbom(),
  postAnalyzeScan: postAnalyzeScan(),
  deleteAnalysis: deleteAnalysis(),
  postAddRepository: postAddRepository(),
  postPatch: postPatch(),
  getResultActivityStream: getResultActivityStream(),
  postScanPatch: postScanPatch(),
  postCreateWorkflow: postCreateWorkflow(),
  putUpdateWorkflow: putUpdateWorkflow(),
  postRepositoryIntegration: postRepositoryIntegration(),
  deleteWorkflow: deleteWorkflow(),
  deleteRepository: deleteRepository(),
  deleteScan: deleteScan(),
  postAnalysisCancellation: postAnalysisCancellation(),
  deleteRepositoryIntegration: deleteRepositoryIntegration(),
  postPush: postPush(),
  postPolarisStatusLookup: postPolarisStatusLookup(),
  getPushesByIds: getPushesByIds(),
  getCodingGuidelines: getCodingGuidelines(),
  postCodingGuideline: postCodingGuideline(),
  patchCodingGuideline: patchCodingGuideline(),
  deleteCodingGuideline: deleteCodingGuideline(),
  getCodingGuidelineEvents: getCodingGuidelineEvents(),
  postSupportBundle: postSupportBundle(),
  getApiRoot: getApiRoot(),
  getSupportBundleStream: getSupportBundleStream(),
}
